import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { LessorCombobox } from "@/components/admin/v2/LessorCombobox";
import {
  ApartmentSearchPicker,
  type PickedApartment,
} from "@/components/admin/v2/ApartmentSearchPicker";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { useApartments } from "@/hooks/v2/useProperties";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  DEFAULT_MANAGEMENT_FEE,
  orderFeeTotals,
} from "@/lib/owner-onboarding-objects";
import {
  setUserPassword,
  setUserActive,
  inviteOwnerToProperty,
  sendUserLoginLink,
  deleteUserAccount,
} from "@/lib/admin-account-actions";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import type { AppRole } from "@/lib/roles";
import { displayStaffTier } from "@/lib/roles";
import {
  discardCreatedOwnerLessor,
  OwnerLessorError,
  resolveEditedOwnerLessor,
  resolveNewOwnerLessor,
} from "@/lib/ensure-owner-lessor";
import { isTestAccount } from "@/lib/is-test-account";
import {
  useTestAccountFilters,
  useRemoveTestAccountFilter,
} from "@/hooks/v2/useTestAccountFilters";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Crown,
  Shield,
  Briefcase,
  Building,
  Building2,
  Users,
  UserPlus,
  Pencil,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  KeyRound,
  MoreHorizontal,
  Eye,
  EyeOff,
  Check,
  Link,
  Trash2,
  Plus,
  Search,
  RotateCcw,
  FileText,
  ExternalLink,
  FlaskConical,
} from "lucide-react";
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { getSiteUrl, getSiteUrlForPath } from "@/lib/site-url";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatSupabaseError } from "@/lib/format-supabase-error";
import {
  fetchAdminProfilesDirectory,
  type AdminProfileDirectoryRow,
} from "@/lib/fetch-admin-profiles-directory";
import { cn } from "@/lib/utils";
import { invalidateOwnerCaches } from "@/hooks/v2/owner-detail-keys";

const ELEVATED_ROLES: AppRole[] = ["super_admin", "tech", "admin", "owner"];

const testEmailSchema = z.string().trim().toLowerCase().email();

function roleBadgeClass(role: string): string {
  switch (role) {
    case "super_admin":
      return "bg-yellow-100 text-yellow-800 border-yellow-300";
    case "tech":
      return "bg-blue-100 text-blue-800 border-blue-300";
    case "admin":
      return "bg-green-100 text-green-800 border-green-300";
    case "owner":
      return "bg-purple-100 text-purple-800 border-purple-300";
    default:
      return "bg-gray-100 text-gray-600 border-gray-300";
  }
}

function roleLabel(
  role: string,
  t: (key: string, fallback: string) => string
): string {
  switch (role) {
    case "super_admin":
      return t("admin.roles.label.superAdmin", "Super Admin");
    case "tech":
      return t("admin.roles.label.tech", "Tech");
    case "admin":
      return t("admin.roles.label.admin", "Admin");
    case "owner":
      return t("admin.roles.label.owner", "Owner");
    case "user":
      return t("admin.roles.label.tenant", "Tenant");
    default:
      return role;
  }
}

// Small dot that leads each role pill — a saturated companion to the softer
// pill fill from roleBadgeClass.
function roleDotClass(role: string): string {
  switch (role) {
    case "super_admin":
      return "bg-yellow-500";
    case "tech":
      return "bg-blue-500";
    case "admin":
      return "bg-green-500";
    case "owner":
      return "bg-purple-500";
    default:
      return "bg-gray-400";
  }
}

// Role-tinted avatar so a person's row reads as one block at a glance.
function roleAvatarClass(role: string): string {
  switch (role) {
    case "super_admin":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-950/50 dark:text-yellow-200";
    case "tech":
      return "bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-200";
    case "admin":
      return "bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-200";
    case "owner":
      return "bg-purple-100 text-purple-800 dark:bg-purple-950/50 dark:text-purple-200";
    default:
      return "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300";
  }
}

// Two-letter initials from a display name, falling back to the email handle.
function initialsOf(name: string | null | undefined, email: string): string {
  const src = (name && name.trim()) || email || "?";
  const parts = src.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

// Role tiers rendered as collapsible sections, in display order. Owners open
// by default; the rest start collapsed. Keys match displayStaffTier().
type StaffTier =
  | "owner"
  | "operator"
  | "admin"
  | "tech"
  | "tenant"
  | "super_admin";
/** People shown per page in the flat directory list. */
const DIRECTORY_PAGE_SIZE = 10;
const GROUP_META: {
  key: StaffTier;
  label: string;
  labelKey: string;
  /** Singular form, for the role badge on a row. */
  singular: string;
  singularKey: string;
  /** What this tier can actually do — shown when its filter is selected. */
  meaning: string;
  meaningKey: string;
  icon: typeof Building;
  tint: string;
}[] = [
  {
    key: "super_admin",
    label: "Super admins",
    labelKey: "admin.roles.group.superAdmin.label",
    singular: "Super Admin",
    singularKey: "admin.roles.group.superAdmin.singular",
    meaning: "Full access, and the only tier that can change roles.",
    meaningKey: "admin.roles.group.superAdmin.meaning",
    icon: Crown,
    tint: "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
  },
  {
    key: "tech",
    label: "Tech",
    labelKey: "admin.roles.group.tech.label",
    singular: "Tech",
    singularKey: "admin.roles.group.tech.singular",
    meaning: "Operations, integrations and audit. No sales tools.",
    meaningKey: "admin.roles.group.tech.meaning",
    icon: Shield,
    tint: "bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300",
  },
  {
    key: "admin",
    label: "Admins",
    labelKey: "admin.roles.group.admin.label",
    singular: "Admin",
    singularKey: "admin.roles.group.admin.singular",
    meaning: "Sales and daily operations. Can invite and edit tenants.",
    meaningKey: "admin.roles.group.admin.meaning",
    icon: Briefcase,
    tint: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
  },
  {
    key: "operator",
    label: "Operators",
    labelKey: "admin.roles.group.operator.label",
    singular: "Operator",
    singularKey: "admin.roles.group.operator.singular",
    meaning: "A SaaS customer running their own brand on the platform.",
    meaningKey: "admin.roles.group.operator.meaning",
    icon: Briefcase,
    tint: "bg-teal-100 text-teal-700 dark:bg-teal-950/50 dark:text-teal-300",
  },
  {
    key: "owner",
    label: "Owners",
    labelKey: "admin.roles.group.owner.label",
    singular: "Owner",
    singularKey: "admin.roles.group.owner.singular",
    meaning: "A dashboard scoped to their own portfolio, nothing else.",
    meaningKey: "admin.roles.group.owner.meaning",
    icon: Building,
    tint: "bg-purple-100 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300",
  },
  {
    key: "tenant",
    label: "Tenants",
    labelKey: "admin.roles.group.tenant.label",
    singular: "Tenant",
    singularKey: "admin.roles.group.tenant.singular",
    meaning: "Their own booking, documents and payments only.",
    meaningKey: "admin.roles.group.tenant.meaning",
    icon: Users,
    tint: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  },
];

/** Tier order for the flat "All" list — most privileged first. */
const TIER_RANK: Record<StaffTier, number> = {
  super_admin: 0,
  tech: 1,
  admin: 2,
  operator: 3,
  owner: 4,
  tenant: 5,
};

type ProfileRow = AdminProfileDirectoryRow;

function ownerPrimaryPropertyId(row: ProfileRow): string {
  const first = row.property_owners?.[0]?.property_id;
  return first != null ? String(first) : "";
}

export function AdminRoleTierOverview({
  excludeTiers = [],
  visibleTiers,
}: {
  /**
   * Tier cards to leave out.
   *
   * Owners & access passes `["owner"]`. The Owner card counted portal ACCOUNTS
   * with the owner role, while that page's Owners tab counts merged owners
   * (accounts joined with billing entities) — so the two showed different
   * numbers under the same word, on the same screen. The tab is the honest
   * count, so the card goes rather than being quietly reconciled.
   */
  excludeTiers?: StaffTier[];
  /** Allow-list variant (COL-352: operators see only the tiers their org can
   *  hold, e.g. ["super_admin","admin"]). When given it wins over excludeTiers.
   *  Undefined = fall back to excludeTiers. Members are RLS-scoped, no query
   *  change. */
  visibleTiers?: Array<
    "super_admin" | "tech" | "admin" | "operator" | "owner" | "tenant"
  >;
} = {}) {
  const { t } = useLanguage();
  const {
    data: users,
    isLoading,
    error: overviewQueryError,
  } = useQuery({
    queryKey: ["admin-settings-role-overview"],
    queryFn: fetchAdminProfilesDirectory,
  });

  const overviewErrorMessage = overviewQueryError
    ? formatSupabaseError(overviewQueryError)
    : null;

  const buckets = useMemo(() => {
    const b = {
      super_admin: [] as ProfileRow[],
      tech: [] as ProfileRow[],
      admin: [] as ProfileRow[],
      operator: [] as ProfileRow[],
      owner: [] as ProfileRow[],
      tenant: [] as ProfileRow[],
    };
    if (!users) return b;
    for (const u of users) {
      const roles = (u.user_roles ?? []).map((r) => r.role);
      const tier = displayStaffTier(roles);
      b[tier].push(u);
    }
    return b;
  }, [users]);

  const tiers: {
    key: keyof typeof buckets;
    title: string;
    subtitle: string;
    accent: string;
    iconBg: string;
    iconText: string;
    icon: typeof Crown;
  }[] = [
    {
      key: "super_admin",
      title: t("admin.roles.overview.superAdmin.title", "Super Admin"),
      subtitle: t(
        "admin.roles.overview.superAdmin.subtitle",
        "Full access & sole role manager"
      ),
      accent: "border-t-amber-400",
      iconBg: "bg-amber-100",
      iconText: "text-amber-600",
      icon: Crown,
    },
    {
      key: "tech",
      title: t("admin.roles.overview.tech.title", "Tech"),
      subtitle: t(
        "admin.roles.overview.tech.subtitle",
        "Operations, integrations, audit"
      ),
      accent: "border-t-sky-500",
      iconBg: "bg-sky-100",
      iconText: "text-sky-600",
      icon: Shield,
    },
    {
      key: "admin",
      title: t("admin.roles.overview.admin.title", "Admin"),
      subtitle: t(
        "admin.roles.overview.admin.subtitle",
        "Sales & daily operations"
      ),
      accent: "border-t-emerald-500",
      iconBg: "bg-emerald-100",
      iconText: "text-emerald-600",
      icon: Briefcase,
    },
    {
      key: "operator",
      title: "Operator",
      subtitle: "SaaS customer, own brand",
      accent: "border-t-teal-500",
      iconBg: "bg-teal-100",
      iconText: "text-teal-600",
      icon: Briefcase,
    },
    {
      key: "owner",
      title: t("admin.roles.overview.owner.title", "Owner"),
      subtitle: t(
        "admin.roles.overview.owner.subtitle",
        "Scoped portfolio dashboard"
      ),
      accent: "border-t-violet-500",
      iconBg: "bg-violet-100",
      iconText: "text-violet-600",
      icon: Building,
    },
  ];

  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("admin.roles.overview.loading", "Loading directory…")}
      </p>
    );
  }

  if (overviewErrorMessage) {
    return (
      <Alert variant="destructive">
        <AlertTitle>
          {t("admin.roles.overview.loadError", "Could not load directory")}
        </AlertTitle>
        <AlertDescription className="font-mono text-xs break-all">
          {overviewErrorMessage}
        </AlertDescription>
      </Alert>
    );
  }

  // visibleTiers (allow-list) wins when provided; otherwise use the
  // excludeTiers (deny-list). Renamed the local to shownTiers to avoid
  // colliding with the visibleTiers prop.
  const shownTiers = visibleTiers
    ? tiers.filter((tier) => visibleTiers.includes(tier.key))
    : tiers.filter((tier) => !excludeTiers.includes(tier.key as StaffTier));

  return (
    <div
      className={cn(
        "grid gap-4 md:grid-cols-2",
        shownTiers.length >= 4 ? "xl:grid-cols-4" : "xl:grid-cols-3"
      )}
    >
      {shownTiers.map((tier) => {
        const Icon = tier.icon;
        const members = buckets[tier.key];
        return (
          <Card
            key={tier.key}
            className={cn(
              "rounded-2xl border border-border/60 border-t-[3px] bg-card shadow-sm overflow-hidden transition-all hover:shadow-md hover:-translate-y-0.5",
              tier.accent
            )}
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div
                  className={cn(
                    "flex h-11 w-11 items-center justify-center rounded-xl",
                    tier.iconBg
                  )}
                >
                  <Icon className={cn("h-5 w-5", tier.iconText)} />
                </div>
                <div className="text-right">
                  <div className="text-2xl font-semibold tabular-nums leading-none">
                    {members.length}
                  </div>
                  <div className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                    {members.length === 1
                      ? t("admin.roles.overview.memberSingular", "member")
                      : t("admin.roles.overview.memberPlural", "members")}
                  </div>
                </div>
              </div>
              <div className="mt-4 space-y-0.5">
                <h3 className="font-semibold leading-tight">{tier.title}</h3>
                <p className="text-xs text-muted-foreground">{tier.subtitle}</p>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

/**
 * Toast copy for a failed billing-entity step. OwnerLessorError codes are
 * things the admin can act on (pick one of several matches); anything else is
 * a Supabase error, shown as such, with `fallback` when it has no message.
 */
function billingEntityErrorMessage(
  e: unknown,
  t: (key: string, fallback?: string) => string,
  fallback: string
): string {
  if (e instanceof OwnerLessorError) {
    if (e.code === "ambiguous")
      return t(
        "admin.roles.billingEntity.errAmbiguous",
        "Several billing entities use this email. Pick the right one under Billing entity."
      );
    if (e.code === "wrong_operator")
      return t(
        "admin.roles.billingEntity.errWrongOperator",
        "That billing entity belongs to another operator than this property. Pick one from the property's operator."
      );
    return t(
      "admin.roles.billingEntity.errNotCreated",
      "The billing entity could not be created. Nothing was changed."
    );
  }
  return formatSupabaseError(e) || fallback;
}

/**
 * "Billing entity" picker shared by every form that makes someone an owner
 * (COL-486). The choice decides who that owner's apartments are invoiced to
 * (see ensureOwnerLessor), so it is spelled out rather than left to a silent
 * default: pick a record — e.g. the company a representative signs for — or
 * leave it and we use the one on file for this email, else create one.
 */
function OwnerBillingEntityField({
  value,
  onChange,
  className,
}: {
  value: string | null;
  onChange: (next: string | null) => void;
  className?: string;
}) {
  const { t } = useLanguage();
  return (
    <div className={cn("space-y-2 max-w-md", className)}>
      <Label>
        {t("admin.roles.billingEntity.label", "Billing entity (invoiced as)")}
      </Label>
      <LessorCombobox
        value={value}
        onChange={onChange}
        placeholder={t(
          "admin.roles.billingEntity.placeholder",
          "Use or create one for this email"
        )}
        noneLabel={t(
          "admin.roles.billingEntity.none",
          "Use or create one for this email"
        )}
        triggerClassName="rounded-xl"
      />
      <p className="text-xs text-muted-foreground">
        {t(
          "admin.roles.billingEntity.hint",
          "Who we invoice for this owner's apartments. Leave empty to use the billing entity with this email, or create a new one from these details. Pick one if they sign for a company."
        )}
      </p>
    </div>
  );
}

function AdminInviteTeamMemberCard({
  isSuperAdmin,
  properties,
  allowOwnerRole = true,
  onInvited,
}: {
  isSuperAdmin: boolean;
  properties: { id: number; title: string; city: string }[] | undefined;
  /**
   * Whether "owner" is offered as a role. False on a directory that excludes
   * owners — inviting one there would create somebody who then shows up on a
   * different tab, which is the confusion this page exists to remove.
   */
  allowOwnerRole?: boolean;
  /** Called after a successful invite, so a host dialog can close itself. */
  onInvited?: () => void;
}) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [inviteRole, setInviteRole] = useState<AppRole>("admin");
  const [invitePropertyId, setInvitePropertyId] = useState("");
  // Owner invites only: who this owner is invoiced as. Null = the billing
  // entity on file for their email, else a new one (COL-486).
  const [inviteLessorId, setInviteLessorId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submitInvite = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      toast.error(
        t("admin.roles.invite.errInvalidEmail", "Enter a valid email address.")
      );
      return;
    }
    if (!isSuperAdmin && inviteRole === "super_admin") {
      toast.error(
        t(
          "admin.roles.invite.errSuperAdminOnly",
          "Only Super Admins can assign the super_admin role."
        )
      );
      return;
    }
    if (inviteRole === "owner" && !invitePropertyId) {
      toast.error(
        t(
          "admin.roles.invite.errPropertyRequired",
          "Select a property for an Owner invite."
        )
      );
      return;
    }

    setSubmitting(true);
    try {
      // Every NEW owner gets a billing entity (COL-486). An account that is
      // already an owner keeps whatever it bills to today unless the admin
      // picked one — see findExistingOwnerAccountId for why.
      let ownerLessor: { lessorId: string; created: boolean } | undefined;
      if (inviteRole === "owner") {
        try {
          ownerLessor = await resolveNewOwnerLessor(
            {
              name: displayName.trim(),
              email: trimmed,
              propertyId: invitePropertyId,
            },
            inviteLessorId
          );
        } catch (lessorErr) {
          toast.error(
            billingEntityErrorMessage(
              lessorErr,
              t,
              t(
                "admin.roles.invite.errBillingEntityFailed",
                "Could not set up the owner's billing entity. Nothing was changed."
              )
            )
          );
          return;
        }
      }
      const body = {
        email: trimmed,
        name: displayName.trim() || undefined,
        role: inviteRole,
        property_id:
          inviteRole === "owner" && invitePropertyId
            ? Number(invitePropertyId)
            : undefined,
        lessor_id: ownerLessor?.lessorId,
        redirect_to: getSiteUrlForPath("/"),
        skip_invite_email: true,
        // This panel exists to set a person's role, so picking a non-owner
        // role here IS the demotion — the one caller that means it. Everywhere
        // else admin-invite-user now leaves an existing owner's role and
        // property assignments alone; see _shared/owner-demotion.ts.
        allow_owner_demotion: true,
      };

      const { data, error } = await supabase.functions.invoke<{
        ok?: boolean;
        error?: string;
        message?: string;
      }>("admin-invite-user", { body });

      if (error || data?.error) {
        // The account half failed — don't leave a billing entity behind that
        // this attempt created (a reused one is never touched).
        if (ownerLessor?.created)
          await discardCreatedOwnerLessor(ownerLessor.lessorId);
        toast.error(
          data?.error ||
            error?.message ||
            t("admin.roles.invite.errRequestFailed", "Invite request failed.")
        );
        return;
      }

      toast.success(
        data?.message ?? t("admin.roles.invite.successInvited", "User invited.")
      );
      setEmail("");
      setDisplayName("");
      setInviteRole("admin");
      setInvitePropertyId("");
      setInviteLessorId(null);
      await queryClient.invalidateQueries({
        queryKey: ["admin-role-mgmt-users"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["admin-settings-role-overview"],
      });
      onInvited?.();
    } catch (e: unknown) {
      toast.error(
        e instanceof Error
          ? e.message
          : t("admin.roles.invite.errFailed", "Invite failed.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="rounded-2xl border-border/60 shadow-sm overflow-hidden border-primary/20">
      <CardHeader className="border-b bg-muted/15">
        <CardTitle className="text-lg flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-primary" />
          {t("admin.roles.invite.title", "Invite team member")}
        </CardTitle>
        <CardDescription>
          {t(
            "admin.roles.invite.description",
            "Send an invitation email to a new team member or update the role of an existing account. The invitee will receive a secure link to set their password and access the platform."
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="invite-email">
              {t("admin.roles.invite.emailLabel", "Email")}
            </Label>
            <Input
              id="invite-email"
              type="email"
              autoComplete="off"
              placeholder="colleague@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="invite-name">
              {t("admin.roles.invite.nameLabel", "Display name (optional)")}
            </Label>
            <Input
              id="invite-name"
              placeholder="Jordan Lee"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label>{t("admin.roles.invite.roleLabel", "Role")}</Label>
            <Select
              value={inviteRole}
              onValueChange={(v) => setInviteRole(v as AppRole)}
            >
              <SelectTrigger className="rounded-xl h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">admin</SelectItem>
                <SelectItem value="tech">tech</SelectItem>
                <SelectItem value="super_admin">
                  {t("admin.roles.label.superAdmin", "Super Admin")}
                </SelectItem>
                {allowOwnerRole ? (
                  <SelectItem value="owner">owner</SelectItem>
                ) : null}
              </SelectContent>
            </Select>
          </div>
        </div>
        {inviteRole === "owner" ? (
          <div className="space-y-2 max-w-md">
            <Label>{t("admin.roles.invite.propertyLabel", "Property")}</Label>
            <Select
              value={invitePropertyId || undefined}
              onValueChange={setInvitePropertyId}
            >
              <SelectTrigger className="rounded-xl h-10">
                <SelectValue
                  placeholder={t(
                    "admin.roles.invite.propertyPlaceholder",
                    "Link owner to a property"
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {properties?.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.title} — {p.city}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
        {inviteRole === "owner" ? (
          <OwnerBillingEntityField
            value={inviteLessorId}
            onChange={setInviteLessorId}
          />
        ) : null}
        <Button
          type="button"
          className="rounded-xl"
          disabled={submitting}
          onClick={() => void submitInvite()}
        >
          {submitting
            ? t("admin.roles.invite.sending", "Sending…")
            : t("admin.roles.invite.submit", "Invite / assign role")}
        </Button>
      </CardContent>
    </Card>
  );
}

export function AdminCreateOwnerAccountCard({
  onSuccess,
  initialLessorId = null,
}: {
  /** Called after an owner account is created (e.g. to close a hosting dialog + refetch). */
  onSuccess?: () => void;
  /**
   * Billing entity to preselect — "Create login" on a billing-only row passes
   * that row's lessor, so the new login lands on the record it was opened
   * from instead of matching (or minting) one by whatever email is typed.
   */
  initialLessorId?: string | null;
}) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [language, setLanguage] = useState("en");
  const [bankHolder, setBankHolder] = useState("");
  const [bankIban, setBankIban] = useState("");
  const [bankBic, setBankBic] = useState("");
  const [bankName, setBankName] = useState("");
  const [lessorId, setLessorId] = useState<string | null>(initialLessorId);
  const [apartment, setApartment] = useState<PickedApartment | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!name.trim()) {
      toast.error(
        t("admin.roles.createOwner.errNameRequired", "Full name is required.")
      );
      return;
    }
    if (!trimmedEmail || !trimmedEmail.includes("@")) {
      toast.error(
        t("admin.roles.invite.errInvalidEmail", "Enter a valid email address.")
      );
      return;
    }
    if (!apartment) {
      toast.error(
        t(
          "admin.roles.createOwner.errApartmentRequired",
          "Please assign the owner to an apartment."
        )
      );
      return;
    }

    setSubmitting(true);
    try {
      // The Owners space is keyed on the `lessors` record, the portal account
      // on profiles/property_owners. Creating only the account left the new
      // owner visible in settings but absent from Owners (and unbillable), so
      // ensure BOTH exist — the picked lessor, else the one on file for this
      // email, else a new one (see ensureOwnerLessor for why this decides who
      // gets invoiced). An email that is ALREADY an owner is left alone unless
      // one was picked — it keeps its current link (existingOwnerLessorId).
      let ownerLessorId: string | undefined;
      try {
        ownerLessorId = (
          await resolveNewOwnerLessor(
            {
              name,
              email: trimmedEmail,
              propertyId: apartment.propertyId,
              phone,
              bankHolder,
              bankIban,
              bankBic,
              bankName,
            },
            lessorId
          )
        )?.lessorId;
      } catch (lessorErr) {
        toast.error(
          billingEntityErrorMessage(
            lessorErr,
            t,
            t(
              "admin.roles.createOwner.errCreateFailed",
              "Could not create the owner."
            )
          )
        );
        return;
      }

      const { data, error } = await supabase.functions.invoke<{
        ok?: boolean;
        error?: string;
        message?: string;
        user_id?: string;
      }>("admin-invite-user", {
        body: {
          email: trimmedEmail,
          name: name.trim(),
          phone: phone.trim() || undefined,
          role: "owner",
          property_id: apartment.propertyId,
          bank_account_holder: bankHolder.trim() || undefined,
          bank_account_iban: bankIban.trim() || undefined,
          bank_account_bic: bankBic.trim() || undefined,
          lessor_id: ownerLessorId,
          app_url: window.location.origin,
          skip_invite_email: true,
        },
      });

      if (error || data?.error) {
        // The lessor is saved and already listed under Owners; only the portal
        // account failed. Say which half landed so the retry makes sense.
        const reason =
          data?.error ||
          error?.message ||
          t(
            "admin.roles.createOwner.errPortalAccountFailed",
            "Failed to create the portal account."
          );
        toast.error(
          `${reason} ${t(
            "admin.roles.createOwner.errRetryHint",
            "The owner record was saved — retry to add their account."
          )}`
        );
        invalidateOwnerCaches(queryClient);
        return;
      }

      // Pin ownership to the SPECIFIC apartment picked (not the whole
      // property the account was just scoped to for portal access) — the
      // apartment picker's whole point is per-unit attribution, so an owner
      // holding one of several units in a building isn't shown as owning
      // all of them.
      if (data?.user_id) {
        const { error: linkErr } = await supabase.rpc(
          "admin_link_owner_apartment",
          { _user_id: data.user_id, _apartment_id: apartment.id }
        );
        if (linkErr) {
          toast.error(
            `${formatSupabaseError(linkErr) || t("admin.roles.createOwner.errApartmentLinkFailed", "The account was created, but linking the apartment failed.")} ${t(
              "admin.roles.createOwner.errApartmentLinkRetryHint",
              "Link the apartment from the owner's profile instead."
            )}`
          );
        }
      }

      toast.success(
        t(
          "admin.roles.createOwner.successCreated",
          "Owner created — listed under Owners. Send them a login link from the ··· menu when ready."
        )
      );
      setName("");
      setEmail("");
      setPhone("");
      setLanguage("en");
      setBankHolder("");
      setBankIban("");
      setBankBic("");
      setBankName("");
      setLessorId(null);
      setApartment(null);
      setShowDetails(false);
      await queryClient.invalidateQueries({
        queryKey: ["admin-role-mgmt-users"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["admin-settings-role-overview"],
      });
      invalidateOwnerCaches(queryClient);
      onSuccess?.();
    } catch (e: unknown) {
      toast.error(
        e instanceof Error
          ? e.message
          : t(
              "admin.roles.createOwner.errCreateAccountFailed",
              "Failed to create account."
            )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="rounded-2xl shadow-sm overflow-hidden border-purple-200 dark:border-purple-800/40">
      <CardHeader className="border-b bg-purple-50/50 dark:bg-purple-950/20">
        <CardTitle className="text-lg flex items-center gap-2">
          <Building2 className="h-5 w-5 text-purple-600" />
          {t("admin.roles.createOwner.title", "Create owner account")}
        </CardTitle>
        <CardDescription>
          {t(
            "admin.roles.createOwner.description",
            "Create a portal account for a property owner and link it to their property. No email is sent — the owner has no access until you send the login link from the ··· menu on their row in the table below."
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6 space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="owner-name">
              {t("admin.roles.createOwner.fullName", "Full name")}{" "}
              <span className="text-destructive">*</span>
            </Label>
            <Input
              id="owner-name"
              placeholder="Maria Müller"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="owner-email">
              {t("admin.roles.invite.emailLabel", "Email")}{" "}
              <span className="text-destructive">*</span>
            </Label>
            <Input
              id="owner-email"
              type="email"
              autoComplete="off"
              placeholder="owner@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-xl"
            />
          </div>
        </div>

        <Collapsible open={showDetails} onOpenChange={setShowDetails}>
          <CollapsibleTrigger className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform",
                showDetails && "rotate-180"
              )}
            />
            {t(
              "admin.roles.createOwner.additionalDetails",
              "Additional details"
            )}{" "}
            <span className="text-xs">
              ({t("admin.roles.createOwner.optional", "optional")})
            </span>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-3 space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="owner-phone">
                  {t("admin.roles.createOwner.phone", "Phone number")}
                </Label>
                <Input
                  id="owner-phone"
                  type="tel"
                  placeholder="+49 89 123 456"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>
                  {t("admin.roles.createOwner.language", "Language")}
                </Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger className="rounded-xl h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">EN — English</SelectItem>
                    <SelectItem value="de">DE — Deutsch</SelectItem>
                    <SelectItem value="fr">FR — Français</SelectItem>
                    <SelectItem value="es">ES — Español</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t(
                  "admin.roles.createOwner.bankDetails",
                  "Bank details for payouts"
                )}
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="owner-bank-holder">
                    {t(
                      "admin.roles.createOwner.accountHolder",
                      "Account holder name"
                    )}
                  </Label>
                  <Input
                    id="owner-bank-holder"
                    placeholder="Maria Müller"
                    value={bankHolder}
                    onChange={(e) => setBankHolder(e.target.value)}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="owner-iban">IBAN</Label>
                  <Input
                    id="owner-iban"
                    placeholder="DE89 3704 0044 0532 0130 00"
                    value={bankIban}
                    onChange={(e) => setBankIban(e.target.value)}
                    className="rounded-xl font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="owner-bic">BIC</Label>
                  <Input
                    id="owner-bic"
                    placeholder="COBADEFFXXX"
                    value={bankBic}
                    onChange={(e) => setBankBic(e.target.value)}
                    className="rounded-xl font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="owner-bank-name">
                    {t("admin.roles.createOwner.bankName", "Bank name")}
                  </Label>
                  <Input
                    id="owner-bank-name"
                    placeholder="Commerzbank"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    className="rounded-xl"
                  />
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Outside "Additional details": who gets invoiced is not an optional
            detail, so the choice stays in view (COL-486). */}
        <OwnerBillingEntityField
          value={lessorId}
          onChange={setLessorId}
          className="max-w-none"
        />

        <div className="space-y-2">
          <Label>
            {t(
              "admin.roles.createOwner.assignApartment",
              "Assign to apartment"
            )}{" "}
            <span className="text-destructive">*</span>
          </Label>
          <ApartmentSearchPicker
            value={apartment}
            onSelect={setApartment}
            placeholder={t(
              "admin.roles.createOwner.selectApartment",
              "Search for an apartment…"
            )}
          />
          <p className="text-xs text-muted-foreground">
            {t(
              "admin.roles.createOwner.apartmentHint",
              "The owner's portal will be scoped to this apartment. You can link additional apartments later."
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            {t(
              "admin.roles.createOwner.representorDefault",
              "Representor: Coliviq GmbH (default) — override this later from Edit Apartment."
            )}
          </p>
        </div>

        <div className="space-y-2">
          <Button
            type="button"
            className="w-full rounded-xl h-11 bg-teal-500 hover:bg-teal-600 text-white gap-2"
            disabled={submitting}
            onClick={() => void submit()}
          >
            <UserPlus className="h-4 w-4" />
            {submitting
              ? t("admin.roles.createOwner.creating", "Creating…")
              : t("admin.roles.createOwner.submit", "Create account")}
          </Button>
          <p className="text-xs text-muted-foreground">
            {t(
              "admin.roles.createOwner.submitHint",
              "Creates the account and links it to the selected property. The owner will appear in the User roles table below immediately."
            )}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/** One owner↔property assignment in the Edit-user modal. */
type OwnerAssignment = {
  property_id: string;
  contract_required: boolean;
  /** owner_contract_templates ids to sign for this property (default: all). */
  template_ids: string[];
};

/**
 * A single property-assignment row: property select + "contract required"
 * switch + template select (when required) + apartment (unit) checkboxes for
 * that property. Its own useApartments call keeps units scoped to the property.
 */
function OwnerAssignmentRow({
  index,
  assignment,
  properties,
  ownerTemplates,
  userId,
  usedPropertyIds,
  apartmentIds,
  canRemove,
  onChange,
  onApartmentsChange,
  onRoomsResolved,
  onRemove,
}: {
  index: number;
  assignment: OwnerAssignment;
  properties: { id: number; title: string; city: string }[] | undefined;
  ownerTemplates: { id: string; name: string }[];
  userId: string;
  usedPropertyIds: string[];
  apartmentIds: number[] | undefined;
  canRemove: boolean;
  onChange: (next: OwnerAssignment) => void;
  onApartmentsChange: (propertyId: string, ids: number[]) => void;
  /** Billable rooms currently selected for this property, for the dialog's
      running total. Reported from here because only this row holds the
      apartment list the counts come from. */
  onRoomsResolved: (propertyId: string, rooms: number) => void;
  onRemove: () => void;
}) {
  const { t } = useLanguage();
  const propNum = assignment.property_id
    ? Number(assignment.property_id)
    : null;
  const { data: apartments = [] } = useApartments(propNum);

  // Initialise this property's unit selection to the units this owner already
  // owns, once, when apartments first load and no selection exists yet.
  useEffect(() => {
    if (!assignment.property_id) return;
    if (apartmentIds !== undefined) return;
    if (apartments.length === 0) return;
    onApartmentsChange(
      assignment.property_id,
      apartments.filter((a) => a.owner_user_id === userId).map((a) => a.id)
    );
  }, [
    apartments,
    assignment.property_id,
    apartmentIds,
    userId,
    onApartmentsChange,
  ]);

  // Report this row's billable rooms up, so the dialog can state what the
  // invite will cost before it is sent. Units with no rooms contribute
  // nothing: admin-invite-owner cannot price them and refuses the invite.
  const selectedRoomCount = apartments
    .filter((a) => (apartmentIds ?? []).includes(a.id))
    .reduce((sum, a) => sum + (a.rooms_count || 0), 0);
  useEffect(() => {
    if (!assignment.property_id) return;
    onRoomsResolved(assignment.property_id, selectedRoomCount);
  }, [assignment.property_id, selectedRoomCount, onRoomsResolved]);

  // Default to all templates when a required assignment starts with none chosen
  // (matches "both documents are signed by default"). Runs once per row after
  // templates load — so unchecking the last box doesn't snap them all back on.
  const defaultedTemplates = useRef(false);
  useEffect(() => {
    if (defaultedTemplates.current) return;
    if (!assignment.contract_required) return;
    if (ownerTemplates.length === 0) return;
    defaultedTemplates.current = true;
    if (assignment.template_ids.length > 0) return;
    onChange({
      ...assignment,
      template_ids: ownerTemplates.map((tpl) => tpl.id),
    });
  }, [ownerTemplates, assignment, onChange]);

  const selectedApartmentIds = apartmentIds ?? [];

  return (
    <div className="rounded-xl border border-border bg-muted/10 p-3 space-y-3">
      <div className="flex items-start gap-2">
        <div className="flex-1 space-y-1.5">
          <Label className="text-xs text-muted-foreground">
            {t("admin.roles.assignment.property", "Property")} {index + 1}
          </Label>
          <Select
            value={assignment.property_id || undefined}
            onValueChange={(v) => onChange({ ...assignment, property_id: v })}
          >
            <SelectTrigger className="rounded-xl h-10">
              <SelectValue
                placeholder={t(
                  "admin.roles.createOwner.selectProperty",
                  "Select a property"
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {properties
                ?.filter(
                  (p) =>
                    !usedPropertyIds.includes(String(p.id)) ||
                    String(p.id) === assignment.property_id
                )
                .map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.title} — {p.city}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="mt-6 shrink-0 text-muted-foreground hover:text-destructive"
            onClick={onRemove}
            aria-label={t(
              "admin.roles.assignment.removeProperty",
              "Remove property"
            )}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Contract required toggle */}
      <div className="flex items-center justify-between gap-3">
        <div className="space-y-0.5">
          <Label className="text-sm">
            {t(
              "admin.roles.assignment.agreementRequired",
              "Owner agreement required"
            )}
          </Label>
          <p className="text-xs text-muted-foreground">
            {t(
              "admin.roles.assignment.agreementHint",
              "Send a contract to sign during onboarding for this property."
            )}
          </p>
        </div>
        <Switch
          checked={assignment.contract_required}
          onCheckedChange={(v) =>
            onChange({ ...assignment, contract_required: v })
          }
        />
      </div>

      {/* Contract templates (only when required) — one or more per property */}
      {assignment.contract_required && (
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">
            {t(
              "admin.roles.assignment.contractTemplates",
              "Contract templates"
            )}
          </Label>
          {ownerTemplates.length === 0 ? (
            <p className="rounded-xl border bg-background px-3 py-2 text-xs text-muted-foreground">
              {t(
                "admin.roles.assignment.noTemplatesYet",
                "No owner contract templates yet."
              )}
            </p>
          ) : (
            <div className="rounded-xl border divide-y bg-background">
              {ownerTemplates.map((tpl) => {
                const checked = assignment.template_ids.includes(tpl.id);
                return (
                  <label
                    key={tpl.id}
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) =>
                        onChange({
                          ...assignment,
                          template_ids:
                            v === true
                              ? [...assignment.template_ids, tpl.id]
                              : assignment.template_ids.filter(
                                  (id) => id !== tpl.id
                                ),
                        })
                      }
                    />
                    <span className="text-sm">{tpl.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Apartment (unit) assignment for this property */}
      {apartments.length > 0 && (
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">
            {t(
              "admin.roles.assignment.assignApartments",
              "Assign to apartment(s)"
            )}
          </Label>
          <div className="rounded-xl border divide-y bg-background">
            {apartments.map((apt) => {
              const checked = selectedApartmentIds.includes(apt.id);
              return (
                <label
                  key={apt.id}
                  className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(v) =>
                      onApartmentsChange(
                        assignment.property_id,
                        v === true
                          ? [...selectedApartmentIds, apt.id]
                          : selectedApartmentIds.filter((id) => id !== apt.id)
                      )
                    }
                  />
                  <span className="text-sm">
                    {apt.label}
                    {apt.floor ? (
                      <span className="text-muted-foreground">
                        {" "}
                        · {apt.floor}
                      </span>
                    ) : null}
                    {/* The number the invite will actually bill.
                        `rooms_count` is allAptRooms.length -- every room row
                        on the unit, hidden ones included -- which is exactly
                        what admin-invite-owner counts when it stamps
                        prefilled_rooms. Showing apartments.num_rooms instead
                        would look right and be wrong: that column is what was
                        planned, and the invite bills what exists.

                        A unit with no rooms cannot be priced at all --
                        admin-invite-owner refuses the invite rather than
                        inventing one room -- so it is called out here rather
                        than shown as a billable "0 Zimmer". */}
                    {apt.rooms_count > 0 ? (
                      <span className="text-muted-foreground">
                        {" "}
                        ·{" "}
                        {t(
                          "admin.roles.assignment.roomCount",
                          "{n} rooms"
                        ).replace("{n}", String(apt.rooms_count))}
                      </span>
                    ) : (
                      <span className="text-amber-600">
                        {" "}
                        ·{" "}
                        {t("admin.roles.assignment.noRooms", "no rooms")}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Send-onboarding-invitation dialog. Lists the owner's assigned properties and
 * sends one invitation per property (Step 3), each linked to that property's
 * selected contract templates. The admin picks which property to send, or sends
 * all that require a contract.
 */
function OwnerInviteDialog({
  row,
  properties,
  onClose,
  onSend,
}: {
  row: ProfileRow;
  properties: { id: number; title: string; city: string }[] | undefined;
  onClose: () => void;
  onSend: (propertyId: string, templateIds: string[]) => Promise<boolean>;
}) {
  const { t } = useLanguage();
  const assignments = row.property_owners ?? [];
  const [templateNames, setTemplateNames] = useState<Record<string, string>>(
    {}
  );
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [sentIds, setSentIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    supabase
      .from("owner_contract_templates")
      .select("id, name")
      .then(({ data }) => {
        if (!data) return;
        const map: Record<string, string> = {};
        data.forEach((tpl: { id: string; name: string }) => {
          map[tpl.id] = tpl.name;
        });
        setTemplateNames(map);
      });
  }, []);

  const propTitle = (pid: number) => {
    const p = properties?.find((x) => x.id === pid);
    return p
      ? `${p.title} — ${p.city}`
      : `${t("admin.roles.assignment.property", "Property")} #${pid}`;
  };

  const send = async (propertyId: string, templateIds: string[]) => {
    setSendingId(propertyId);
    try {
      const ok = await onSend(propertyId, templateIds);
      if (ok) setSentIds((prev) => new Set(prev).add(propertyId));
    } finally {
      setSendingId(null);
    }
  };

  const contractProps = assignments.filter((a) => a.contract_required);

  const sendAll = async () => {
    for (const a of contractProps) {
      await send(String(a.property_id), a.contract_template_ids ?? []);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="rounded-2xl sm:max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {t("admin.roles.ownerInvite.title", "Send onboarding invitation")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "admin.roles.ownerInvite.description",
              "One invitation per property — the owner signs that property's agreement, prefilled with its details."
            )}{" "}
            {row.email}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 overflow-y-auto flex-1 pr-1">
          {assignments.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t(
                "admin.roles.ownerInvite.noProperties",
                "No properties assigned. Add properties in Edit user first."
              )}
            </p>
          ) : (
            assignments.map((a) => {
              const pid = String(a.property_id);
              const ids = a.contract_template_ids ?? [];
              const names = ids.map((id) => templateNames[id]).filter(Boolean);
              const sent = sentIds.has(pid);
              return (
                <div
                  key={pid}
                  className="rounded-xl border border-border bg-muted/10 p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">
                        {propTitle(a.property_id)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {a.contract_required
                          ? names.length > 0
                            ? names.join(", ")
                            : t(
                                "admin.roles.ownerInvite.allActiveTemplates",
                                "All active templates"
                              )
                          : t(
                              "admin.roles.ownerInvite.noContractRequired",
                              "No contract required"
                            )}
                      </p>
                    </div>
                    {a.contract_required ? (
                      <Button
                        type="button"
                        size="sm"
                        variant={sent ? "outline" : "default"}
                        className="rounded-xl shrink-0 gap-1.5"
                        disabled={sendingId === pid}
                        onClick={() => void send(pid, ids)}
                      >
                        {sendingId === pid ? (
                          t("admin.roles.ownerInvite.sending", "Sending…")
                        ) : sent ? (
                          <>
                            <Check className="h-3.5 w-3.5" />
                            {t("admin.roles.ownerInvite.resend", "Resend")}
                          </>
                        ) : (
                          t("admin.roles.ownerInvite.send", "Send")
                        )}
                      </Button>
                    ) : (
                      <Badge
                        variant="outline"
                        className="shrink-0 text-muted-foreground"
                      >
                        {t(
                          "admin.roles.ownerInvite.notRequired",
                          "Not required"
                        )}
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0 shrink-0 pt-2 border-t border-border">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            onClick={onClose}
          >
            {t("common.close", "Close")}
          </Button>
          {contractProps.length > 1 ? (
            <Button
              type="button"
              className="rounded-xl"
              disabled={!!sendingId}
              onClick={() => void sendAll()}
            >
              {t("admin.roles.ownerInvite.sendAll", "Send all")} (
              {contractProps.length})
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EditUserDialog({
  row,
  properties,
  isSuperAdmin,
  title,
  onClose,
  onSaved,
}: {
  row: ProfileRow;
  properties: { id: number; title: string; city: string }[] | undefined;
  isSuperAdmin: boolean;
  /**
   * Heading. Defaults to "Edit user" for Roles & access, which edits any
   * account. The Owners area passes its own so the dialog is headed by the
   * thing the admin clicked ("Edit login & access") rather than by a generic
   * noun that reads as a different screen.
   */
  title?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLanguage();
  const effective = (() => {
    const roles = (row.user_roles ?? []).map((r) => r.role);
    if (roles.includes("super_admin")) return "super_admin";
    if (roles.includes("tech")) return "tech";
    if (roles.includes("admin")) return "admin";
    if (roles.includes("owner")) return "owner";
    return "user";
  })();
  const primaryOwner = row.property_owners?.[0];
  /** Already an owner when the dialog opened — see the billing step in handleSave. */
  const wasOwner = (row.user_roles ?? []).some((r) => r.role === "owner");

  const [name, setName] = useState(row.name ?? "");
  const [phone, setPhone] = useState(row.phone ?? "");
  const [role, setRole] = useState<AppRole>(effective);
  // Multiple property assignments — each with its own contract requirement +
  // template (COL owner onboarding spec). Falls back to one blank row.
  const [assignments, setAssignments] = useState<OwnerAssignment[]>(() => {
    const existing = row.property_owners ?? [];
    if (existing.length === 0) {
      return [{ property_id: "", contract_required: true, template_ids: [] }];
    }
    return existing.map((po) => ({
      property_id: String(po.property_id),
      contract_required: po.contract_required ?? true,
      template_ids: po.contract_template_ids ?? [],
    }));
  });
  // Apartment (unit) selection per assigned property, keyed by property_id.
  const [apartmentIdsByProperty, setApartmentIdsByProperty] = useState<
    Record<string, number[]>
  >({});
  // Billable rooms per assigned property, reported by each assignment row.
  const [roomsByProperty, setRoomsByProperty] = useState<
    Record<string, number>
  >({});
  const handleRoomsResolved = useCallback(
    (propertyId: string, rooms: number) =>
      setRoomsByProperty((prev) =>
        prev[propertyId] === rooms ? prev : { ...prev, [propertyId]: rooms }
      ),
    []
  );
  // Rooms across every CURRENTLY assigned property.
  //
  // Summing roomsByProperty directly would keep counting a row the admin has
  // since removed, or the property a row used to point at before it was
  // re-pointed -- the map is only ever added to. Reading it through the live
  // assignment list means a stale entry simply stops being looked up.
  const assignedRoomTotal = assignments.reduce(
    (sum, a) =>
      a.property_id ? sum + (roomsByProperty[a.property_id] ?? 0) : sum,
    0
  );
  const [address, setAddress] = useState(row.address ?? "");
  const [managementFee, setManagementFee] = useState(
    row.management_fee != null ? String(row.management_fee) : ""
  );
  const [managementFeeRooms, setManagementFeeRooms] = useState(
    row.management_fee_rooms != null ? String(row.management_fee_rooms) : ""
  );
  // One calculation for the admin preview and the owner's order: the entered
  // rate is NET per room per month, VAT goes on top, and the parts are rounded
  // once so the two screens cannot disagree by a cent.
  const feePreview = orderFeeTotals(
    (Number(managementFee) || 0) * (Number(managementFeeRooms) || 0)
  );
  // The rate as typed, falling back to the standard one when the box is blank
  // -- exactly what the invite does.
  const effectiveFee = Number(managementFee) || DEFAULT_MANAGEMENT_FEE;
  const assignedFee = orderFeeTotals(effectiveFee * assignedRoomTotal);
  const [bankHolder, setBankHolder] = useState(
    primaryOwner?.bank_account_name ?? ""
  );
  const [bankIban, setBankIban] = useState(
    primaryOwner?.bank_account_iban ?? ""
  );
  const [bankBic, setBankBic] = useState(primaryOwner?.bank_account_bic ?? "");
  const [lessorId, setLessorId] = useState<string | null>(
    primaryOwner?.lessor_id ?? null
  );
  const [saving, setSaving] = useState(false);

  const [ownerTemplates, setOwnerTemplates] = useState<
    { id: string; name: string }[]
  >([]);
  const [templateVisibility, setTemplateVisibility] = useState<
    Record<string, boolean>
  >({});

  // Load owner contract templates + existing visibility for this owner
  useEffect(() => {
    if (role !== "owner" || !row.id) return;
    // Load all templates
    supabase
      .from("owner_contract_templates")
      .select("id, name")
      .order("created_at", { ascending: true })
      .then(({ data: templates }) => {
        if (!templates) return;
        setOwnerTemplates(templates);
        // Default all enabled
        const defaults: Record<string, boolean> = {};
        templates.forEach((tpl: { id: string }) => {
          defaults[tpl.id] = true;
        });
        // Load saved visibility
        supabase
          .from("owner_document_visibility")
          .select("document_key, is_enabled")
          .eq("user_id", row.id)
          .then(({ data: vis }) => {
            if (vis) {
              vis.forEach(
                (v: { document_key: string; is_enabled: boolean }) => {
                  defaults[v.document_key] = v.is_enabled;
                }
              );
            }
            setTemplateVisibility(defaults);
          });
      });
  }, [row.id, role]);

  const ELEVATED_ROLES_LIST: AppRole[] = [
    "super_admin",
    "tech",
    "admin",
    "owner",
  ];

  const handleSave = async () => {
    // Dedupe assignments by property (last wins), keep only rows with a property.
    const validAssignments = Array.from(
      new Map(
        assignments.filter((a) => a.property_id).map((a) => [a.property_id, a])
      ).values()
    );
    if (role === "owner" && validAssignments.length === 0) {
      toast.error(
        t(
          "admin.roles.editUser.errPropertyRequired",
          "Assign at least one property for the Owner role."
        )
      );
      return;
    }
    if (
      role === "owner" &&
      ownerTemplates.length > 0 &&
      validAssignments.some(
        (a) => a.contract_required && a.template_ids.length === 0
      )
    ) {
      toast.error(
        t(
          "admin.roles.editUser.errTemplateRequired",
          "Select at least one contract template for each property that requires an agreement."
        )
      );
      return;
    }
    // The fee is net PER ROOM, so a count that is not a whole number above
    // zero cannot be priced. The same value is copied onto pending onboarding
    // tokens below, so refusing it here keeps admin-invite-owner's guard from
    // being routed around ("0" is truthy after trim; "abc" would write NaN).
    const roomsRaw = managementFeeRooms.trim();
    if (
      roomsRaw &&
      !(Number.isInteger(Number(roomsRaw)) && Number(roomsRaw) > 0)
    ) {
      toast.error(
        t(
          "admin.roles.editUser.errRoomsInvalid",
          "Room count must be a whole number above zero."
        )
      );
      return;
    }
    setSaving(true);
    try {
      // Becoming an owner means getting a billing entity (COL-486): the picked
      // one, else the one on file for this email, else a new one. Resolved
      // before any write so a failure leaves the account untouched. An account
      // that was ALREADY an owner is left as it is — minting a lessor on a
      // routine edit would move its apartments' invoices (ensureOwnerLessor).
      const ownerLessorId = await resolveEditedOwnerLessor(
        { role, wasOwner },
        {
          name,
          email: row.email ?? "",
          phone,
          bankHolder,
          bankIban,
          bankBic,
        },
        lessorId,
        validAssignments.map((a) => a.property_id)
      );
      // A retry after a later failure reuses it instead of creating another.
      if (ownerLessorId !== lessorId) setLessorId(ownerLessorId);

      // Update profile
      const { error: profileErr } = await supabase
        .from("profiles")
        .update({
          name: name.trim() || null,
          phone: phone.trim() || null,
          address: address.trim() || null,
          management_fee: managementFee.trim()
            ? Number(managementFee.trim())
            : null,
          management_fee_rooms: managementFeeRooms.trim()
            ? Number(managementFeeRooms.trim())
            : null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", row.id);
      if (profileErr) throw profileErr;

      // Sync name/phone/bank/fee into any pending onboarding tokens for this email
      if (
        name.trim() ||
        phone.trim() ||
        bankIban.trim() ||
        managementFee.trim()
      ) {
        const feeValue = managementFee.trim()
          ? Number(managementFee.trim())
          : null;
        // Propagate the rate ONLY when the admin actually edited it.
        //
        // This field is pre-filled from profiles.management_fee, so without
        // this guard saving an unrelated edit -- a corrected phone number --
        // would push the profile's rate over every pending invite. A grouped
        // invite legitimately carries a different rate per object (that is why
        // uniformFeePerRoom can return null), so those are negotiated values,
        // not a cache of the profile: silently flattening them to one number
        // would reprice an owner's order with nobody having asked for it.
        const feeChanged =
          managementFee.trim() !==
          (row.management_fee != null ? String(row.management_fee) : "");
        const { data: syncedTokens } = await supabase
          .from("owner_onboarding_tokens")
          .update({
            prefilled_name: name.trim() || null,
            prefilled_phone: phone.trim() || null,
            prefilled_bank_holder: bankHolder.trim() || null,
            prefilled_iban: bankIban.trim() || null,
            prefilled_bic: bankBic.trim() || null,
            ...(feeChanged && feeValue !== null
              ? { prefilled_fee_amount: feeValue }
              : {}),
            ...(managementFeeRooms.trim()
              ? { prefilled_rooms: Number(managementFeeRooms.trim()) }
              : {}),
          })
          .eq("email", row.email)
          .eq("status", "pending")
          .select("id");

        // A grouped invite prices each object from its OWN row in
        // owner_onboarding_token_properties, and objectsFromGroupedRows prefers
        // that value over the token-level fallback. Updating only the parent
        // token above therefore changed nothing the owner would ever see on a
        // grouped invite -- the Leistungen step and the order summary kept
        // quoting the rate snapshotted when the invite was sent.
        //
        // The fee is a RATE (per room, per month) and this dialog offers a
        // single field for it, so it applies to every object on the invite.
        // The room COUNT is deliberately not propagated here: each child row
        // carries its own count for its own apartment, and overwriting fifteen
        // of them with one number would misprice the order.
        //
        // Cast as elsewhere for this table (see the RPC call in
        // src/pages/Onboarding.tsx): owner_onboarding_token_properties
        // postdates the last generated-types run, so it is absent from
        // supabase-types.ts and the typed builder resolves its columns to
        // `never`.
        const tokenIds = (syncedTokens ?? []).map((t) => t.id);
        if (feeChanged && feeValue !== null && tokenIds.length > 0) {
          const { error: groupedFeeError } = await (
            supabase.from as unknown as (table: string) => {
              update: (values: Record<string, unknown>) => {
                in: (
                  column: string,
                  values: unknown[]
                ) => Promise<{ error: { message: string } | null }>;
              };
            }
          )("owner_onboarding_token_properties")
            .update({ prefilled_fee_amount: feeValue })
            .in("token_id", tokenIds);
          if (groupedFeeError) throw groupedFeeError;
        }
      }

      // Update role
      await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", row.id)
        .in("role", ELEVATED_ROLES_LIST);
      if (role !== "user") {
        const { error: roleErr } = await supabase
          .from("user_roles")
          .insert({ user_id: row.id, role });
        if (roleErr) throw roleErr;
      }

      // Replace property + apartment assignments atomically. Bank + lessor are
      // owner-level (shared payout account) and written onto every assigned
      // property row. A single RPC does the delete-then-insert plus apartment
      // ownership in one transaction, so a mid-way failure rolls back and can't
      // strand the owner with zero assignments. For non-owners we pass empty
      // arrays, which clears any leftover assignments the same way.
      const ownerAssignments =
        role === "owner"
          ? validAssignments.map((a) => ({
              property_id: Number(a.property_id),
              contract_required: a.contract_required,
              template_ids: a.contract_required ? a.template_ids : [],
            }))
          : [];
      const selectedApartmentIds =
        role === "owner"
          ? validAssignments.flatMap(
              (a) => apartmentIdsByProperty[a.property_id] ?? []
            )
          : [];

      const { error: assignErr } = await supabase.rpc(
        "admin_replace_owner_assignments",
        {
          _user_id: row.id,
          _assignments: ownerAssignments,
          _bank_holder: bankHolder.trim() || null,
          _bank_iban: bankIban.trim() || null,
          _bank_bic: bankBic.trim() || null,
          _lessor_id: ownerLessorId,
          _apartment_ids: selectedApartmentIds,
        }
      );
      if (assignErr) throw assignErr;

      // Save document visibility for owner
      if (role === "owner") {
        for (const [key, enabled] of Object.entries(templateVisibility)) {
          await supabase
            .from("owner_document_visibility")
            .upsert(
              { user_id: row.id, document_key: key, is_enabled: enabled },
              { onConflict: "user_id,document_key" }
            );
        }
      }

      toast.success(t("admin.roles.editUser.successUpdated", "User updated."));
      onSaved();
      onClose();
    } catch (e: unknown) {
      // Surface the concrete DB/RPC error (PostgREST errors are plain objects,
      // not Error instances — the old instanceof check swallowed their message,
      // e.g. a 404 when admin_replace_owner_assignments is missing on the DB).
      toast.error(
        billingEntityErrorMessage(
          e,
          t,
          t("admin.roles.editUser.errSaveFailed", "Failed to save changes.")
        )
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="rounded-2xl sm:max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {title ?? t("admin.roles.editUser.title", "Edit user")}
          </DialogTitle>
          <DialogDescription>{row.email}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 overflow-y-auto flex-1 pr-1">
          {/* Name + Phone */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="edit-name">
                {t("admin.roles.createOwner.fullName", "Full name")}
              </Label>
              <Input
                id="edit-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-phone">
                {t("admin.roles.createOwner.phone", "Phone number")}
              </Label>
              <Input
                id="edit-phone"
                type="tel"
                placeholder="+49 89 123 456"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="rounded-xl"
              />
            </div>
          </div>

          {/* Address */}
          <div className="space-y-1.5">
            <Label htmlFor="edit-address">
              {t("admin.roles.editUser.address", "Address")}
            </Label>
            <Input
              id="edit-address"
              placeholder="Musterstraße 1, 80331 München"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="rounded-xl"
            />
          </div>

          {/* Role */}
          <div className="space-y-1.5">
            <Label>{t("admin.roles.invite.roleLabel", "Role")}</Label>
            <Select value={role} onValueChange={(v) => setRole(v as AppRole)}>
              <SelectTrigger className="rounded-xl h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">tenant</SelectItem>
                <SelectItem value="admin">admin</SelectItem>
                <SelectItem value="tech">tech</SelectItem>
                {isSuperAdmin && (
                  <SelectItem value="super_admin">
                    {t("admin.roles.label.superAdmin", "Super Admin")}
                  </SelectItem>
                )}
                <SelectItem value="owner">owner</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Property assignments (owners only) — one or more properties, each
              with its own contract requirement + template. */}
          {role === "owner" && (
            <div className="space-y-2">
              <Label>
                {t(
                  "admin.roles.editUser.assignProperties",
                  "Assign to properties"
                )}{" "}
                <span className="text-destructive">*</span>
              </Label>
              <div className="space-y-3">
                {assignments.map((assignment, index) => (
                  <OwnerAssignmentRow
                    key={index}
                    index={index}
                    assignment={assignment}
                    properties={properties}
                    ownerTemplates={ownerTemplates}
                    userId={row.id}
                    usedPropertyIds={assignments
                      .filter((_, i) => i !== index)
                      .map((a) => a.property_id)
                      .filter(Boolean)}
                    apartmentIds={
                      apartmentIdsByProperty[assignment.property_id]
                    }
                    onRoomsResolved={handleRoomsResolved}
                    canRemove={assignments.length > 1}
                    onChange={(next) =>
                      setAssignments((prev) =>
                        prev.map((a, i) => (i === index ? next : a))
                      )
                    }
                    onApartmentsChange={(propertyId, ids) =>
                      setApartmentIdsByProperty((prev) => ({
                        ...prev,
                        [propertyId]: ids,
                      }))
                    }
                    onRemove={() =>
                      setAssignments((prev) =>
                        prev.filter((_, i) => i !== index)
                      )
                    }
                  />
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-xl gap-1.5"
                onClick={() =>
                  setAssignments((prev) => [
                    ...prev,
                    {
                      property_id: "",
                      contract_required: true,
                      template_ids: [],
                    },
                  ])
                }
              >
                <Plus className="h-4 w-4" />
                {t("admin.roles.editUser.addProperty", "Add property")}
              </Button>
            </div>
          )}

          {/* Management fee (owners only) */}
          {role === "owner" && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="edit-mgmt-fee">
                  {t(
                    "admin.roles.editUser.managementFee",
                    "Management fee (€ / Zimmer / Monat)"
                  )}
                </Label>
                <Input
                  id="edit-mgmt-fee"
                  type="number"
                  min={0}
                  placeholder={`e.g. ${DEFAULT_MANAGEMENT_FEE}`}
                  value={managementFee}
                  onChange={(e) => setManagementFee(e.target.value)}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-mgmt-fee-rooms">
                  {t(
                    "admin.roles.editUser.numberOfRooms",
                    "Number of rooms (Zimmer)"
                  )}
                </Label>
                <Input
                  id="edit-mgmt-fee-rooms"
                  type="number"
                  min={1}
                  step={1}
                  placeholder="e.g. 3"
                  value={managementFeeRooms}
                  onChange={(e) => setManagementFeeRooms(e.target.value)}
                  className="rounded-xl"
                />
                {/* What the invite will actually bill, from the units
                    selected above rather than the single room box: a grouped
                    invite prices each apartment on its own room count, so one
                    typed number could never describe four of them. Uses the
                    fee as typed, so it moves while the admin edits it, and
                    orderFeeTotals -- the same helper the owner's order summary
                    and both Auftragsbestaetigung generators use -- so this
                    cannot quote a different total from the one the owner
                    accepts. */}
                {assignedRoomTotal > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "admin.roles.editUser.assignedRoomsTotal",
                      "{rooms} rooms selected · {rate} € net per room · {net} € net / month"
                    )
                      .replace("{rooms}", String(assignedRoomTotal))
                      .replace("{rate}", String(effectiveFee))
                      .replace("{net}", assignedFee.totalNet.toFixed(2))}
                  </p>
                )}
                {/* This preview used to divide the entered fee by 1.19, i.e.
                    read it as a GROSS rate. Every other surface treats
                    prefilled_fee_amount as NET per room and adds 19 % on top --
                    owner-vacancy.ts says so, orderFeeTotals does it, and both
                    Auftragsbestaetigung generators print it that way. So an
                    admin typing 49 over 3 rooms was shown "123.53 netto /
                    147.00 inkl." while the owner's order said "147,00 netto /
                    174,93". Same input, two answers. It now runs the owner's
                    own calculation. */}
                {managementFee && managementFeeRooms && (
                  <p className="text-xs font-medium text-teal-600">
                    {t("admin.roles.editUser.total", "Total")}:{" "}
                    {feePreview.totalNet.toFixed(2)} € / Monat netto ·{" "}
                    {feePreview.totalGross.toFixed(2)} € inkl. MwSt.
                  </p>
                )}
                {/* The fallback must stay byte-identical to the row seeded in
                    20260910190000_i18n_batch5a_owners_roles_new_keys.sql: this
                    key resolves from the translations table, so editing the
                    fallback alone changes nothing a user sees and leaves the
                    German saying something else. Saying "net per room, per
                    month" here -- which is what this field actually is, and
                    what the owner is charged -- needs that row updated in both
                    languages, i.e. a migration. Flagged on the PR rather than
                    done silently. */}
                <p className="text-xs text-muted-foreground">
                  {t(
                    "admin.roles.editUser.feeHint",
                    "Pre-fills the fee and room count in the owner's onboarding order summary."
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Bank details (owners only) */}
          {role === "owner" && (
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t(
                  "admin.roles.createOwner.bankDetails",
                  "Bank details for payouts"
                )}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-bank-holder">
                    {t(
                      "admin.roles.createOwner.accountHolder",
                      "Account holder name"
                    )}
                  </Label>
                  <Input
                    id="edit-bank-holder"
                    placeholder="Maria Müller"
                    value={bankHolder}
                    onChange={(e) => setBankHolder(e.target.value)}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-iban">IBAN</Label>
                  <Input
                    id="edit-iban"
                    placeholder="DE89 3704 0044 0532 0130 00"
                    value={bankIban}
                    onChange={(e) => setBankIban(e.target.value)}
                    className="rounded-xl font-mono"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-bic">BIC</Label>
                  <Input
                    id="edit-bic"
                    placeholder="COBADEFFXXX"
                    value={bankBic}
                    onChange={(e) => setBankBic(e.target.value)}
                    className="rounded-xl font-mono"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Lessor link (owners only). Someone becoming an owner picks the
              billing entity they are invoiced as (COL-486). */}
          {role === "owner" && !wasOwner && (
            <OwnerBillingEntityField
              value={lessorId}
              onChange={setLessorId}
              className="max-w-none space-y-1.5"
            />
          )}
          {role === "owner" && wasOwner && (
            <div className="space-y-1.5">
              <Label>
                {t(
                  "admin.roles.createOwner.linkedRecord",
                  "Linked owner record"
                )}
              </Label>
              <LessorCombobox
                value={lessorId}
                onChange={setLessorId}
                placeholder={t(
                  "admin.roles.createOwner.linkPlaceholder",
                  "Link to an owner entity…"
                )}
                noneLabel={t(
                  "admin.roles.createOwner.noOwnerLinked",
                  "No owner linked"
                )}
                triggerClassName="rounded-xl"
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  "admin.roles.editUser.linkHint",
                  "Link this owner to their lessor entity to share representor and bank defaults."
                )}
              </p>
            </div>
          )}

          {/* Owner document visibility */}
          {role === "owner" && ownerTemplates.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                {t("admin.roles.editUser.ownerDocuments", "Owner Documents")}
              </p>
              <div className="flex flex-wrap gap-2">
                {ownerTemplates.map((tpl) => (
                  <label
                    key={tpl.id}
                    className="flex-1 min-w-[140px] flex items-center gap-2 rounded-xl border border-border p-2.5 cursor-pointer hover:bg-muted/30 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={templateVisibility[tpl.id] ?? true}
                      onChange={(e) =>
                        setTemplateVisibility((prev) => ({
                          ...prev,
                          [tpl.id]: e.target.checked,
                        }))
                      }
                      className="rounded border-gray-300 text-teal-500 focus:ring-teal-500 shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="text-xs font-medium truncate">
                        {tpl.name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {(templateVisibility[tpl.id] ?? true)
                          ? t("admin.roles.editUser.visible", "Visible")
                          : t("admin.roles.editUser.hidden", "Hidden")}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0 shrink-0 pt-2 border-t border-border">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            onClick={onClose}
            disabled={saving}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            className="rounded-xl"
            onClick={() => void handleSave()}
            disabled={saving}
          >
            {saving
              ? t("admin.roles.editUser.saving", "Saving…")
              : t("admin.roles.editUser.saveChanges", "Save changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AdminRoleManagementPanel({
  excludeTiers = [],
}: {
  /**
   * Role tiers to leave out of this directory entirely.
   *
   * Owners & access passes `["owner"]`: owners have their own tab on that page,
   * with more about them than this list can show (apartments, invoices,
   * onboarding), so listing them again here is the same person twice with the
   * poorer view second. Excluding them upstream is what keeps the count chips,
   * the group sections and the pager all telling the same story — the panel
   * already drops empty groups and zero-count chips on its own.
   */
  excludeTiers?: StaffTier[];
} = {}) {
  const {
    user: currentUser,
    isSuperAdmin,
    canActAs,
    hasSalesAdminRole,
    resetPassword,
    actAsOwner,
  } = useAuth();
  const { t } = useLanguage();
  const { data: testAccountFilters } = useTestAccountFilters();
  const removeTestAccountFilter = useRemoveTestAccountFilter();
  const dynamicTestEmails = useMemo(
    () => (testAccountFilters ?? []).map((f) => f.email),
    [testAccountFilters]
  );
  const ownersExcluded = excludeTiers.includes("owner");
  // Joined so the memo below re-runs on CONTENT change, not on a caller passing
  // a fresh array literal every render.
  const excludeKey = excludeTiers.join(",");
  const canAssignRoles = isSuperAdmin || hasSalesAdminRole;
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingById, setEditingById] = useState<Record<string, boolean>>({});
  const [openPropRows, setOpenPropRows] = useState<Record<string, boolean>>({});
  const [roleFilter, setRoleFilter] = useState<StaffTier | "all" | "test">(
    "all"
  );
  const [addTestEmailOpen, setAddTestEmailOpen] = useState(false);
  const [testEmailInput, setTestEmailInput] = useState("");
  const [creatingTestAccount, setCreatingTestAccount] = useState(false);
  const [editDialogRow, setEditDialogRow] = useState<ProfileRow | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteRow, setInviteRow] = useState<ProfileRow | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<{
    userId: string;
    email: string;
    nextRole: AppRole;
  } | null>(null);

  const [pendingDelete, setPendingDelete] = useState<{
    userId: string;
    email: string;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [pendingToggleActive, setPendingToggleActive] = useState<{
    userId: string;
    email: string;
    activate: boolean;
  } | null>(null);
  const [togglingActive, setTogglingActive] = useState(false);

  const [pwTarget, setPwTarget] = useState<{
    mode: "self" | "other" | "reset";
    userId: string;
    email: string;
  } | null>(null);
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [showPwNew, setShowPwNew] = useState(false);
  const [showPwConfirm, setShowPwConfirm] = useState(false);

  const pwLenOk = pwNew.length >= 6;
  const confirmFilled = pwConfirm.length > 0;
  const passwordsMismatch = confirmFilled && pwNew !== pwConfirm;
  const passwordsMatch = confirmFilled && pwNew === pwConfirm && pwLenOk;
  const canSavePassword =
    pwLenOk && confirmFilled && pwNew === pwConfirm && !pwBusy;

  const closePwModal = () => {
    setPwTarget(null);
    setPwNew("");
    setPwConfirm("");
    setPwBusy(false);
    setShowPwNew(false);
    setShowPwConfirm(false);
  };

  const submitPasswordChange = async () => {
    if (!pwTarget) return;
    if (pwNew.length < 6) {
      toast.error(
        t(
          "admin.roles.password.errTooShort",
          "Password must be at least 6 characters."
        )
      );
      return;
    }
    if (pwNew !== pwConfirm) {
      toast.error(
        t("admin.roles.password.errMismatch", "Passwords do not match.")
      );
      return;
    }
    setPwBusy(true);
    try {
      if (pwTarget.mode === "self") {
        const { error } = await resetPassword(pwNew);
        if (error) return;
        closePwModal();
        return;
      }

      const res = await setUserPassword(pwTarget.userId, pwNew);
      if (!res.ok) {
        toast.error(
          res.error ||
            t("admin.roles.password.errSetFailed", "Failed to set password")
        );
        return;
      }
      toast.success(
        res.message ?? t("admin.roles.password.updated", "Password updated.")
      );
      closePwModal();
    } finally {
      setPwBusy(false);
    }
  };

  const { data: properties } = useQuery({
    queryKey: ["admin-role-mgmt-properties"],
    enabled: canAssignRoles,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("properties")
        .select("id, title, city")
        .order("title");
      if (error) throw error;
      return data;
    },
  });

  const {
    data: allUsers,
    isLoading,
    error: usersQueryError,
  } = useQuery({
    queryKey: ["admin-role-mgmt-users"],
    enabled: canAssignRoles,
    queryFn: fetchAdminProfilesDirectory,
  });

  // Drop excluded tiers once, here, so everything downstream — chip counts,
  // groups, "All", pagination — agrees without each needing its own guard.
  const users = useMemo(() => {
    if (!allUsers || excludeTiers.length === 0) return allUsers;
    const skip = new Set(excludeTiers);
    return allUsers.filter(
      (u) =>
        // "operator" (COL-353) is never in excludeTiers, so the cast only
        // narrows the widened tier union back to what the Set can hold.
        !skip.has(
          displayStaffTier((u.user_roles ?? []).map((r) => r.role)) as StaffTier
        )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsers, excludeKey]);

  const usersErrorMessage = usersQueryError
    ? formatSupabaseError(usersQueryError)
    : null;

  const filteredUsers = useMemo(() => {
    if (!users?.length) return [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
    );
  }, [users, searchQuery]);

  // Bucket the (search-filtered) users into role tiers for the collapsible
  // sections. displayStaffTier collapses the multi-role set to one tier.
  const grouped = useMemo(() => {
    const g: Record<StaffTier, ProfileRow[]> = {
      owner: [],
      operator: [],
      admin: [],
      tech: [],
      tenant: [],
      super_admin: [],
    };
    for (const u of filteredUsers) {
      const roles = (u.user_roles ?? []).map((r) => r.role);
      g[displayStaffTier(roles)].push(u);
    }
    return g;
  }, [filteredUsers]);

  // Test/CoLiviq accounts, by email — independent of role tier, since a test
  // account can hold any role. Sourced from allUsers, not the tier-excluded
  // filteredUsers: when this panel is called with excludeTiers=["owner"]
  // (Directory page), an owner whose email is registered as a test account
  // would otherwise never appear as a row here, making "Remove test label"
  // unreachable for them. Not removed from the tier buckets above (this
  // directory's counts are separate from the Bookings & Tenants exclusion,
  // which is where the predicate actually affects tenant tallies).
  const testAccountUsers = useMemo(() => {
    if (!allUsers?.length) return [];
    const matches = allUsers.filter((u) =>
      isTestAccount(u.email, dynamicTestEmails)
    );
    const q = searchQuery.trim().toLowerCase();
    if (!q) return matches;
    return matches.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
    );
  }, [allUsers, dynamicTestEmails, searchQuery]);

  // A dynamically-registered email has no directory row at all when it
  // doesn't match any real profile — e.g. a typo, or an email that was never
  // actually signed up. Surfacing these directly in the tab (not just inside
  // the add-email dialog) is what makes that visible instead of silently
  // doing nothing.
  const testFilterIdByEmail = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of testAccountFilters ?? [])
      m.set(f.email.trim().toLowerCase(), f.id);
    return m;
  }, [testAccountFilters]);

  const unmatchedTestEmails = useMemo(() => {
    // Matched against allUsers, not the tier-excluded `users` — otherwise an
    // owner's email (excluded from this panel when excludeTiers=["owner"])
    // would show as "No account" even though the profile exists.
    const matched = new Set(
      (allUsers ?? [])
        .map((u) => u.email?.trim().toLowerCase())
        .filter((e): e is string => !!e)
    );
    const q = searchQuery.trim().toLowerCase();
    return (testAccountFilters ?? []).filter((f) => {
      const email = f.email.trim().toLowerCase();
      if (matched.has(email)) return false;
      return !q || email.includes(q);
    });
  }, [testAccountFilters, allUsers, searchQuery]);

  // Chip counts come from the full directory (not the search-filtered set) so
  // the numbers stay stable while typing.
  const roleCounts = useMemo(() => {
    const c: Record<StaffTier | "all" | "test", number> = {
      all: users?.length ?? 0,
      owner: 0,
      operator: 0,
      admin: 0,
      tech: 0,
      tenant: 0,
      super_admin: 0,
      test: 0,
    };
    for (const u of users ?? []) {
      const roles = (u.user_roles ?? []).map((r) => r.role);
      c[displayStaffTier(roles)]++;
    }
    // Test accounts can hold a tier excluded from `users` above (e.g. an
    // owner) — count against allUsers so the chip matches testAccountUsers.
    for (const u of allUsers ?? []) {
      if (isTestAccount(u.email, dynamicTestEmails)) c.test++;
    }
    return c;
  }, [users, allUsers, dynamicTestEmails]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  /**
   * The rows the flat list shows, before paging.
   *
   * "All" is sorted by tier so the list still reads as a hierarchy — most
   * privileged first — now that the collapsible per-role sections are gone.
   * Within a tier, people are ordered by name so the list is stable between
   * loads instead of following whatever order the query returned.
   */
  const visibleUsers = useMemo(() => {
    const source =
      roleFilter === "all"
        ? filteredUsers
        : roleFilter === "test"
          ? testAccountUsers
          : (grouped[roleFilter] ?? []);
    return [...source].sort((a, b) => {
      const tierDelta =
        TIER_RANK[displayStaffTier((a.user_roles ?? []).map((r) => r.role))] -
        TIER_RANK[displayStaffTier((b.user_roles ?? []).map((r) => r.role))];
      if (tierDelta !== 0) return tierDelta;
      return (a.name ?? a.email ?? "").localeCompare(b.name ?? b.email ?? "");
    });
  }, [filteredUsers, grouped, roleFilter, testAccountUsers]);

  const listTotalPages = Math.max(
    1,
    Math.ceil(visibleUsers.length / DIRECTORY_PAGE_SIZE)
  );
  const listPage = Math.min(Math.max(1, page), listTotalPages);
  const listStart = (listPage - 1) * DIRECTORY_PAGE_SIZE;
  const pagedUsers = visibleUsers.slice(
    listStart,
    listStart + DIRECTORY_PAGE_SIZE
  );

  // A filter change means a different list, so page 1 — otherwise switching to
  // a short tier from page 3 lands on nothing.
  useEffect(() => {
    setPage(1);
  }, [roleFilter]);

  const [drafts, setDrafts] = useState<
    Record<string, { role: AppRole; propertyId: string }>
  >({});

  const elevatedForUser = (u: ProfileRow): AppRole => {
    const roles = (u.user_roles ?? []).map((r) => r.role);
    if (roles.includes("super_admin")) return "super_admin";
    if (roles.includes("tech")) return "tech";
    if (roles.includes("admin")) return "admin";
    if (roles.includes("owner")) return "owner";
    return "user";
  };

  const ownerPropertyEntries = (
    row: ProfileRow,
    effective: AppRole
  ): { title: string; city: string }[] => {
    if (effective !== "owner") return [];
    return (row.property_owners ?? []).map((po) => {
      const p = properties?.find((x) => x.id === Number(po.property_id));
      return p
        ? { title: p.title, city: p.city ?? "" }
        : { title: `Property #${po.property_id}`, city: "" };
    });
  };

  const rowDraftDirty = (row: ProfileRow): boolean => {
    const d = drafts[row.id];
    if (!d) return false;
    const effRole = elevatedForUser(row);
    const effProp = ownerPrimaryPropertyId(row);
    if (d.role !== effRole) return true;
    if (d.role === "owner") return (d.propertyId || "") !== effProp;
    return false;
  };

  const startEdit = (row: ProfileRow) => {
    setEditingById((prev) => ({ ...prev, [row.id]: true }));
    setDrafts((prev) => ({
      ...prev,
      [row.id]: {
        role: elevatedForUser(row),
        propertyId: ownerPrimaryPropertyId(row),
      },
    }));
  };

  const cancelEdit = (rowId: string) => {
    setEditingById((prev) => {
      const n = { ...prev };
      delete n[rowId];
      return n;
    });
    setDrafts((prev) => {
      const n = { ...prev };
      delete n[rowId];
      return n;
    });
  };

  const flushSave = async (target: ProfileRow, force?: boolean) => {
    const d = drafts[target.id];
    const nextRole = d?.role ?? elevatedForUser(target);

    const prevElevated = elevatedForUser(target);
    if (!force && prevElevated !== "user" && nextRole === "user") {
      setPendingRevoke({
        userId: target.id,
        email: target.email,
        nextRole,
      });
      return;
    }

    try {
      const { error: delErr } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", target.id)
        .in("role", ELEVATED_ROLES);
      if (delErr) throw delErr;

      if (nextRole !== "owner") {
        const { error: poClearErr } = await supabase
          .from("property_owners")
          .delete()
          .eq("user_id", target.id);
        if (poClearErr) throw new Error(poClearErr.message);
      }

      if (nextRole !== "user") {
        const { error: insErr } = await supabase
          .from("user_roles")
          .insert({ user_id: target.id, role: nextRole });
        if (insErr) throw insErr;
      }

      // Owner property assignments are managed in the Edit dialog (multi-property
      // aware, saved atomically). The inline quick-edit only changes the role and
      // must never touch property_owners for an owner — doing so previously wiped
      // every other property the owner held.

      if (target.id === currentUser?.id) {
        await supabase.auth.refreshSession();
      }

      toast.success(t("admin.roles.editUser.roleUpdated", "Role updated."));
      queryClient.invalidateQueries({ queryKey: ["admin-role-mgmt-users"] });
      queryClient.invalidateQueries({
        queryKey: ["admin-settings-role-overview"],
      });
      setEditingById((prev) => {
        const n = { ...prev };
        delete n[target.id];
        return n;
      });
      setDrafts((prev) => {
        const n = { ...prev };
        delete n[target.id];
        return n;
      });
    } catch (e: unknown) {
      const msg =
        e instanceof Error
          ? e.message
          : t("admin.roles.editUser.roleUpdateFailed", "Failed to update role");
      toast.error(msg);
    }
  };

  const sendLoginLink = async (
    _userId: string,
    email: string,
    role?: string,
    name?: string
  ) => {
    const res = await sendUserLoginLink({ email, role, name });
    if (!res.ok) {
      toast.error(
        res.error ||
          t(
            "admin.roles.editUser.loginLinkFailed",
            "Failed to send login link."
          )
      );
      return;
    }
    toast.success(
      res.message ??
        `${t("admin.roles.editUser.loginLinkSent", "Login link sent to")} ${email}.`
    );
  };

  const sendOnboardingLink = async (
    email: string,
    propertyId: string,
    name?: string,
    templateIds?: string[]
  ): Promise<boolean> => {
    const res = await inviteOwnerToProperty({
      email,
      propertyId: Number(propertyId),
      name,
      contractTemplateIds: templateIds,
    });
    if (!res.ok) {
      toast.error(
        res.error ||
          t(
            "admin.roles.editUser.onboardingLinkFailed",
            "Failed to send onboarding link."
          )
      );
      return false;
    }
    toast.success(
      `${t("admin.roles.editUser.onboardingLinkSent", "Onboarding link sent to")} ${email}.`
    );
    queryClient.invalidateQueries({ queryKey: ["admin-role-mgmt-users"] });
    return true;
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const res = await deleteUserAccount(pendingDelete.userId);
      if (!res.ok) {
        // Surfaces domain guards (e.g. the "user still owns bookings" guard)
        // unwrapped by the shared helper, not the generic non-2xx message.
        toast.error(
          res.error ||
            t("admin.roles.editUser.deleteUserFailed", "Failed to delete user.")
        );
        return;
      }
      toast.success(
        `${pendingDelete.email} ${t("admin.roles.editUser.hasBeenDeleted", "has been deleted.")}`
      );
      setPendingDelete(null);
      queryClient.invalidateQueries({ queryKey: ["admin-role-mgmt-users"] });
      queryClient.invalidateQueries({
        queryKey: ["admin-settings-role-overview"],
      });
    } finally {
      setDeleting(false);
    }
  };

  const confirmToggleActive = async () => {
    if (!pendingToggleActive) return;
    setTogglingActive(true);
    try {
      const res = await setUserActive(
        pendingToggleActive.userId,
        pendingToggleActive.activate
      );
      if (!res.ok) {
        toast.error(
          res.error ||
            t(
              "admin.roles.editUser.statusUpdateFailed",
              "Failed to update account status."
            )
        );
        return;
      }
      toast.success(
        pendingToggleActive.activate
          ? `${pendingToggleActive.email} ${t("admin.roles.editUser.hasBeenActivated", "has been activated.")}`
          : `${pendingToggleActive.email} ${t("admin.roles.editUser.hasBeenDeactivated", "has been deactivated.")}`
      );
      setPendingToggleActive(null);
      queryClient.invalidateQueries({ queryKey: ["admin-role-mgmt-users"] });
    } finally {
      setTogglingActive(false);
    }
  };

  if (!canAssignRoles) return null;

  // One person in the flat directory list. The role is carried by a badge next
  // to the name — there is no group header implying it any more; the property
  // assignment sits under the identity.
  const renderUserRow = (row: ProfileRow) => {
    const effective = elevatedForUser(row);
    const entries = ownerPropertyEntries(row, effective);
    const propsOpen = !!openPropRows[row.id];
    const rowTierMeta = GROUP_META.find(
      (g) =>
        g.key === displayStaffTier((row.user_roles ?? []).map((r) => r.role))
    );

    const statusNode = (() => {
      if (effective !== "owner") {
        if (effective === "user" || !row.is_active)
          return <span className="text-sm text-muted-foreground">—</span>;
        return (
          <span className="inline-flex items-center gap-1.5 text-sm text-green-700 dark:text-green-400">
            <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
            {t("admin.roles.active", "Active")}
          </span>
        );
      }
      const map = {
        completed: {
          label: t("admin.roles.onboardingStatus.onboarded", "Onboarded"),
          dot: "bg-green-500",
          text: "text-green-700 dark:text-green-400",
        },
        pending: {
          label: t("admin.roles.onboardingStatus.invited", "Invited"),
          dot: "bg-blue-500",
          text: "text-blue-700 dark:text-blue-400",
        },
        expired: {
          label: t("admin.roles.onboardingStatus.linkExpired", "Link expired"),
          dot: "bg-orange-500",
          text: "text-orange-700 dark:text-orange-400",
        },
      } as const;
      const s = map[row.onboarding_token_status as keyof typeof map] ?? {
        label: t("admin.roles.onboardingStatus.notInvited", "Not invited"),
        dot: "bg-gray-300 dark:bg-gray-600",
        text: "text-muted-foreground",
      };
      return (
        <span
          className={cn("inline-flex items-center gap-1.5 text-sm", s.text)}
        >
          <span className={cn("h-1.5 w-1.5 rounded-full", s.dot)} />
          {s.label}
        </span>
      );
    })();

    const propertyNode = (() => {
      if (entries.length === 0) return null;
      const propRow = (p: { title: string; city: string }, i: number) => (
        <div key={i} className="flex items-center gap-1.5 text-sm">
          <Building2 className="h-3.5 w-3.5 shrink-0 opacity-60" />
          <span className="font-medium">{p.title}</span>
          {p.city ? (
            <span className="text-muted-foreground">· {p.city}</span>
          ) : null}
        </div>
      );
      if (entries.length === 1) {
        const p = entries[0];
        return (
          <Badge
            variant="outline"
            className="rounded-full font-normal text-xs gap-1.5 pl-2 pr-2.5 max-w-full border-purple-200/70 bg-purple-50/70 text-purple-900 dark:border-purple-800/50 dark:bg-purple-950/40 dark:text-purple-100"
          >
            <Building2 className="h-3 w-3 shrink-0 opacity-60" />
            <span className="font-medium truncate max-w-[150px]">
              {p.title}
            </span>
            {p.city ? (
              <span className="opacity-60 whitespace-nowrap">· {p.city}</span>
            ) : null}
          </Badge>
        );
      }
      return (
        <Collapsible
          open={propsOpen}
          onOpenChange={(o) =>
            setOpenPropRows((prev) => ({ ...prev, [row.id]: o }))
          }
        >
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border py-0.5 pl-2 pr-2 text-xs font-medium transition-colors",
                "border-purple-200/70 bg-purple-50/70 text-purple-900 hover:bg-purple-100/70",
                "dark:border-purple-800/50 dark:bg-purple-950/40 dark:text-purple-100 dark:hover:bg-purple-900/40"
              )}
            >
              <Building2 className="h-3.5 w-3.5 shrink-0 opacity-70" />
              {entries.length} properties
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 opacity-70 transition-transform",
                  propsOpen && "rotate-180"
                )}
              />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-1.5">
            <div className="flex flex-col gap-1 border-l-2 border-purple-200/70 pl-2.5 dark:border-purple-800/50">
              {entries.map(propRow)}
            </div>
          </CollapsibleContent>
        </Collapsible>
      );
    })();

    return (
      <div
        key={row.id}
        className={cn(
          "group flex items-start gap-4 px-4 py-3",
          !row.is_active && "opacity-60"
        )}
      >
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-medium",
              roleAvatarClass(effective)
            )}
            aria-hidden
          >
            {initialsOf(row.name, row.email)}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium truncate">{row.name || "—"}</span>
              {/* The role used to be implied by the collapsible group this row
                  sat in. The list is flat now, so without this badge a row
                  says nothing about what the person can reach. */}
              <span
                className={cn(
                  "shrink-0 rounded-full px-2 py-px text-[10px] font-semibold uppercase tracking-wide",
                  rowTierMeta?.tint
                )}
              >
                {rowTierMeta
                  ? t(rowTierMeta.singularKey, rowTierMeta.singular)
                  : null}
              </span>
              {currentUser?.id === row.id && (
                <span className="rounded-full border border-border/70 bg-muted/50 px-1.5 py-px text-[10px] font-normal text-muted-foreground">
                  {t("admin.roles.you", "You")}
                </span>
              )}
              {!row.is_active && (
                <Badge
                  variant="outline"
                  className="text-xs font-normal text-red-600 border-red-300 bg-red-50 dark:bg-red-950/40 dark:border-red-900/60 dark:text-red-300"
                >
                  {t("admin.roles.inactive", "Inactive")}
                </Badge>
              )}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {row.email}
            </div>
            {propertyNode ? <div className="mt-1.5">{propertyNode}</div> : null}
          </div>
        </div>

        <div className="hidden shrink-0 self-center sm:block sm:w-32">
          {statusNode}
        </div>

        <div className="flex shrink-0 items-center gap-2 self-center opacity-70 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-xl gap-1.5"
            onClick={() => setEditDialogRow(row)}
          >
            <Pencil className="h-3.5 w-3.5" />
            {t("common.edit", "Edit")}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-xl px-2.5"
                aria-label={t(
                  "admin.roles.editUser.moreActions",
                  "More actions"
                )}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl">
              {elevatedForUser(row) === "owner" ? (
                <DropdownMenuItem
                  className="rounded-lg gap-2 cursor-pointer text-teal-600 focus:text-teal-600"
                  onClick={() => setInviteRow(row)}
                >
                  <Link className="h-4 w-4" />
                  {t(
                    "admin.roles.editUser.sendOnboardingInvitation",
                    "Send onboarding invitation…"
                  )}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  className="rounded-lg gap-2 cursor-pointer text-teal-600 focus:text-teal-600"
                  onClick={() =>
                    void sendLoginLink(
                      row.id,
                      row.email,
                      elevatedForUser(row) ?? undefined,
                      row.name ?? undefined
                    )
                  }
                >
                  <Link className="h-4 w-4" />
                  {t("admin.owners.actions.sendLoginLink", "Send login link")}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {currentUser?.id === row.id ? (
                <DropdownMenuItem
                  className="rounded-lg gap-2 cursor-pointer"
                  onClick={() =>
                    setPwTarget({
                      mode: "self",
                      userId: row.id,
                      email: row.email,
                    })
                  }
                >
                  <KeyRound className="h-4 w-4" />
                  {t(
                    "admin.roles.editUser.changeMyPassword",
                    "Change my password"
                  )}
                </DropdownMenuItem>
              ) : (
                <>
                  <DropdownMenuItem
                    className="rounded-lg gap-2 cursor-pointer"
                    onClick={() =>
                      setPwTarget({
                        mode: "other",
                        userId: row.id,
                        email: row.email,
                      })
                    }
                  >
                    <KeyRound className="h-4 w-4" />
                    {t("admin.owners.actions.setPassword", "Set password")}
                  </DropdownMenuItem>
                  {isSuperAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="rounded-lg gap-2 cursor-pointer"
                        onClick={() =>
                          setPwTarget({
                            mode: "reset",
                            userId: row.id,
                            email: row.email,
                          })
                        }
                      >
                        <RotateCcw className="h-4 w-4" />
                        {t(
                          "admin.owners.actions.resetPassword",
                          "Reset password"
                        )}
                      </DropdownMenuItem>
                    </>
                  )}
                </>
              )}
              {/* Acting as an owner is open to admins too; deactivating and
                  deleting an account stay super-admin-only, so the two are
                  gated separately. */}
              {canActAs &&
                currentUser?.id !== row.id &&
                elevatedForUser(row) === "owner" && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="rounded-lg gap-2 cursor-pointer"
                      onClick={() => void actAsOwner(row.id)}
                    >
                      <Eye className="h-4 w-4" />
                      {t("admin.owners.actions.actAsOwner", "Act as owner")}
                    </DropdownMenuItem>
                  </>
                )}
              {isSuperAdmin && currentUser?.id !== row.id ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className={cn(
                      "rounded-lg gap-2 cursor-pointer",
                      row.is_active
                        ? "text-orange-600 focus:text-orange-600"
                        : "text-emerald-600 focus:text-emerald-600"
                    )}
                    onClick={() =>
                      setPendingToggleActive({
                        userId: row.id,
                        email: row.email,
                        activate: !row.is_active,
                      })
                    }
                  >
                    {row.is_active ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                    {row.is_active
                      ? t(
                          "admin.owners.actions.deactivateAccount",
                          "Deactivate account"
                        )
                      : t(
                          "admin.owners.actions.activateAccount",
                          "Activate account"
                        )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="rounded-lg gap-2 cursor-pointer text-destructive focus:text-destructive"
                    onClick={() =>
                      setPendingDelete({
                        userId: row.id,
                        email: row.email,
                      })
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                    {t("admin.roles.editUser.deleteUser", "Delete user")}
                  </DropdownMenuItem>
                </>
              ) : null}
              {/* Only a dynamically-registered email can be un-labeled here —
                  the static @coliviq.de/seed rules aren't rows in
                  test_account_filters, so there's nothing to remove for them. */}
              {testFilterIdByEmail.has(
                row.email?.trim().toLowerCase() ?? ""
              ) ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="rounded-lg gap-2 cursor-pointer"
                    onClick={async () => {
                      const filterId = testFilterIdByEmail.get(
                        row.email.trim().toLowerCase()
                      );
                      if (!filterId) return;
                      try {
                        await removeTestAccountFilter.mutateAsync(filterId);
                        toast.success(
                          t(
                            "admin.roles.editUser.noLongerTestAccount",
                            "No longer marked as a test account."
                          )
                        );
                      } catch (error: any) {
                        toast.error(
                          formatSupabaseError(error) ||
                            t(
                              "admin.roles.editUser.removeTestLabelFailed",
                              "Failed to remove test label"
                            )
                        );
                      }
                    }}
                  >
                    <FlaskConical className="h-4 w-4" />
                    {t(
                      "admin.roles.editUser.removeTestLabel",
                      "Remove test label"
                    )}
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Creating an owner belongs wherever owners are listed. With owners
          excluded, that is another surface (the Owners tab's "Add owner"), so
          this form would offer to create people who then appear elsewhere. */}
      {!ownersExcluded && (
        <div id="admin-invite-anchor">
          <AdminCreateOwnerAccountCard />
        </div>
      )}

      <section className="rounded-2xl border border-border/60 bg-card shadow-sm">
        {/* Toolbar — title, search and Invite on one line. The old card header
            sat in its own bordered block above a second bordered block; one
            surface with a single toolbar is the same information without the
            box-in-a-box. */}
        <div className="flex flex-wrap items-center gap-3 border-b border-border/60 px-4 py-3 sm:px-5">
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-semibold">
              {ownersExcluded
                ? t(
                    "admin.roles.mgmt.staffAndTenants",
                    "Staff & tenant accounts"
                  )
                : t("admin.roles.mgmt.userRoles", "User roles")}
            </h3>
            <p className="truncate text-xs text-muted-foreground">
              {roleCounts.all}{" "}
              {roleCounts.all === 1
                ? t("admin.roles.mgmt.personSingular", "person")
                : t("admin.roles.mgmt.personPlural", "people")}
              {ownersExcluded
                ? ` · ${t("admin.roles.mgmt.ownersOnTab", "owners are on the Owners tab")}`
                : null}
            </p>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t(
                "admin.roles.mgmt.searchPlaceholder",
                "Search name or email"
              )}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 rounded-full pl-9"
            />
          </div>
          {/* With owners excluded, the "Create owner account" card this used
              to scroll to isn't rendered, so scrolling was a dead click — and
              it was the wrong target anyway: inviting a colleague is not
              creating an owner. Open the invite form instead. */}
          <Button
            type="button"
            variant="outline"
            className="h-9 shrink-0 gap-1.5 rounded-full"
            onClick={() => setAddTestEmailOpen(true)}
          >
            <FlaskConical className="h-4 w-4" />
            {t("admin.directory.addTestEmail", "Add test email")}
          </Button>
          {/* Primary, not outline: this is the only action in the toolbar, and
              an outline button beside an outline search field left the one
              thing you can DO here reading as decoration. Matches "Add owner"
              and "Review" — the other primary actions on this page. */}
          <Button
            type="button"
            className="h-9 shrink-0 gap-1.5 rounded-full"
            onClick={() => {
              if (ownersExcluded) {
                setInviteOpen(true);
                return;
              }
              document
                .getElementById("admin-invite-anchor")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            <UserPlus className="h-4 w-4" />
            {t("admin.roles.mgmt.invite", "Invite")}
          </Button>
        </div>

        <Dialog open={addTestEmailOpen} onOpenChange={setAddTestEmailOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {t(
                  "admin.directory.addTestEmailDialogTitle",
                  "Register a test email"
                )}
              </DialogTitle>
              <DialogDescription>
                {t(
                  "admin.directory.addTestEmailDialogDescription",
                  "Excluded from the default Bookings & Tenants view and bucketed under this Directory tab. If nobody has this email yet, a tenant-tier account is created for it — no invite email is sent."
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="test-email-input">
                {t("admin.roles.invite.emailLabel", "Email")}
              </Label>
              <Input
                id="test-email-input"
                type="email"
                placeholder="name@example.com"
                value={testEmailInput}
                onChange={(e) => setTestEmailInput(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setAddTestEmailOpen(false);
                  setTestEmailInput("");
                }}
              >
                {t("common.cancel", "Cancel")}
              </Button>
              <Button
                type="button"
                disabled={
                  !testEmailSchema.safeParse(testEmailInput).success ||
                  creatingTestAccount
                }
                onClick={async () => {
                  const parsed = testEmailSchema.safeParse(testEmailInput);
                  if (!parsed.success) {
                    toast.error(
                      t(
                        "admin.roles.invite.errInvalidEmail",
                        "Enter a valid email address."
                      )
                    );
                    return;
                  }
                  const email = parsed.data;
                  // This needs to be a real Directory entry — visible under
                  // Everyone and the Test/CoLiviq pill — not just a filter
                  // row with nothing behind it. One request to
                  // admin-invite-user both resolves/creates the account
                  // (tenant-tiered, silent — no role assigned, no invite
                  // email sent) and marks it as a test account, atomically:
                  // preserve_existing_role means an email that turns out to
                  // already belong to someone is left completely untouched
                  // rather than demoted, and mark_test_account means the two
                  // effects can't partially succeed the way two separate
                  // client-side calls could.
                  setCreatingTestAccount(true);
                  try {
                    const { data, error } = await supabase.functions.invoke<{
                      ok?: boolean;
                      error?: string;
                      existed?: boolean;
                    }>("admin-invite-user", {
                      body: {
                        email,
                        role: "user",
                        skip_invite_email: true,
                        preserve_existing_role: true,
                        mark_test_account: true,
                      },
                    });
                    if (error) {
                      toast.error(
                        error.message ||
                          t(
                            "admin.roles.testEmail.errAddFailed",
                            "Failed to add test email."
                          )
                      );
                      return;
                    }
                    if (data?.error) {
                      toast.error(data.error);
                      return;
                    }
                    if (!data?.existed) {
                      await queryClient.invalidateQueries({
                        queryKey: ["admin-role-mgmt-users"],
                      });
                      await queryClient.invalidateQueries({
                        queryKey: ["admin-settings-role-overview"],
                      });
                    }
                    await queryClient.invalidateQueries({
                      queryKey: ["test-account-filters"],
                    });
                    toast.success(
                      data?.existed
                        ? t(
                            "admin.roles.testEmail.markedAsTest",
                            "Marked as a test account."
                          )
                        : t(
                            "admin.roles.testEmail.created",
                            "Test account created."
                          )
                    );
                    setAddTestEmailOpen(false);
                    setTestEmailInput("");
                  } catch (error: any) {
                    toast.error(
                      formatSupabaseError(error) ||
                        t(
                          "admin.roles.testEmail.errAddFailedShort",
                          "Failed to add test email"
                        )
                    );
                  } finally {
                    setCreatingTestAccount(false);
                  }
                }}
              >
                {creatingTestAccount
                  ? t("admin.roles.testEmail.adding", "Adding…")
                  : t("common.add", "Add")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {usersErrorMessage ? (
          <div className="p-4 sm:p-5">
            <Alert variant="destructive">
              <AlertTitle>
                {t(
                  "admin.roles.mgmt.loadProfilesFailed",
                  "Could not load profiles"
                )}
              </AlertTitle>
              <AlertDescription className="font-mono text-xs break-all">
                {usersErrorMessage}
              </AlertDescription>
            </Alert>
          </div>
        ) : isLoading ? (
          <p className="p-5 text-sm text-muted-foreground">
            {t("admin.roles.loadingUsers", "Loading users…")}
          </p>
        ) : (
          <>
            {/* Role filter. This is now the ONLY place a per-role count is
                shown: the tier cards above repeated these same numbers, and
                the collapsible group headers repeated them a third time. */}
            {roleCounts.all > 0 || roleCounts.test > 0 ? (
              <div className="space-y-2 border-b border-border/60 px-4 py-3 sm:px-5">
                <div className="flex flex-wrap gap-1.5">
                  {(
                    [
                      {
                        key: "all",
                        label: t("admin.roles.everyone", "Everyone"),
                        icon: null,
                        tint: "",
                      },
                      ...GROUP_META.map((g) => ({
                        key: g.key,
                        label: t(g.labelKey, g.label),
                        icon: g.icon,
                        tint: g.tint,
                      })),
                      {
                        key: "test",
                        label: t(
                          "admin.directory.testAccountsTab",
                          "Test / coliviq accounts"
                        ),
                        icon: FlaskConical,
                        tint: "bg-orange-100 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300",
                      },
                    ] as {
                      key: StaffTier | "all" | "test";
                      label: string;
                      icon: typeof Building | null;
                      tint: string;
                    }[]
                  )
                    .filter((c) => c.key === "all" || roleCounts[c.key] > 0)
                    .map((c) => {
                      const activeChip = roleFilter === c.key;
                      const Icon = c.icon;
                      return (
                        <button
                          key={c.key}
                          type="button"
                          aria-pressed={activeChip}
                          onClick={() => setRoleFilter(c.key)}
                          className={cn(
                            "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                            activeChip
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border/70 bg-background text-muted-foreground hover:bg-muted/50"
                          )}
                        >
                          {Icon ? (
                            <span
                              className={cn(
                                "flex h-5 w-5 items-center justify-center rounded-full",
                                activeChip
                                  ? "bg-primary-foreground/15 text-primary-foreground"
                                  : c.tint
                              )}
                            >
                              <Icon className="h-3 w-3" />
                            </span>
                          ) : null}
                          {c.label}
                          <span
                            className={cn(
                              "tabular-nums",
                              activeChip ? "opacity-80" : "opacity-60"
                            )}
                          >
                            {roleCounts[c.key]}
                          </span>
                        </button>
                      );
                    })}
                </div>
                {/* What the selected tier can do. The three tier cards existed
                    to say this; one line under the active filter says it
                    without three permanent boxes of chrome. */}
                <p className="text-xs text-muted-foreground">
                  {roleFilter === "all"
                    ? t(
                        "admin.roles.everyoneMeaning",
                        "Everyone with a login, most privileged first. Pick a role to see what it can reach."
                      )
                    : roleFilter === "test"
                      ? t(
                          "admin.directory.testAccountsMeaning",
                          "Internal and test accounts, excluded from tenant counts elsewhere by default."
                        )
                      : (() => {
                          const g = GROUP_META.find(
                            (g) => g.key === roleFilter
                          );
                          return g ? t(g.meaningKey, g.meaning) : null;
                        })()}
                </p>
              </div>
            ) : null}

            {roleFilter === "test" && unmatchedTestEmails.length > 0 ? (
              <div className="border-b border-border/60 px-4 py-3 sm:px-5">
                <p className="mb-2 text-xs font-medium text-muted-foreground">
                  Registered, but no matching account yet
                </p>
                <div className="space-y-1.5">
                  {unmatchedTestEmails.map((f) => (
                    <div
                      key={f.id}
                      className="flex items-center justify-between gap-2 rounded-xl border border-dashed border-border/60 px-3 py-2"
                    >
                      <div className="flex items-center gap-2 truncate text-sm">
                        <FlaskConical className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="truncate">{f.email}</span>
                        <Badge variant="outline" className="text-[10px]">
                          {t("admin.roles.testEmail.noAccount", "No account")}
                        </Badge>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                        aria-label={`${t("admin.roles.testEmail.remove", "Remove")} ${f.email}`}
                        onClick={async () => {
                          try {
                            await removeTestAccountFilter.mutateAsync(f.id);
                            toast.success(
                              t(
                                "admin.roles.testEmail.removed",
                                "Test email removed."
                              )
                            );
                          } catch (error: any) {
                            toast.error(
                              formatSupabaseError(error) ||
                                t(
                                  "admin.roles.testEmail.errRemoveFailed",
                                  "Failed to remove test email"
                                )
                            );
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {visibleUsers.length === 0 ? (
              unmatchedTestEmails.length === 0 ? (
                <div className="px-5 py-14 text-center text-sm text-muted-foreground">
                  {searchQuery.trim() ? (
                    `${t("admin.roles.mgmt.noOneMatches", "No one matches")} "${searchQuery.trim()}".`
                  ) : (
                    <>
                      {t(
                        "admin.roles.mgmt.noProfilesLoaded",
                        "No profiles loaded yet. Use the “Invite” button, or confirm your account can read"
                      )}{" "}
                      <code className="text-xs">public.profiles</code>{" "}
                      {t("admin.roles.mgmt.underRls", "under RLS.")}
                    </>
                  )}
                </div>
              ) : null
            ) : (
              <>
                {/* One flat list. The accordion made every role a click away
                    and hid how many people there actually are. */}
                <div className="divide-y divide-border/60">
                  {pagedUsers.map(renderUserRow)}
                </div>
                {visibleUsers.length > DIRECTORY_PAGE_SIZE ? (
                  <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 px-4 py-3 sm:px-5">
                    <p className="text-xs tabular-nums text-muted-foreground">
                      {listStart + 1}–
                      {Math.min(
                        listStart + DIRECTORY_PAGE_SIZE,
                        visibleUsers.length
                      )}{" "}
                      {t("admin.roles.mgmt.of", "of")} {visibleUsers.length}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 rounded-lg px-2"
                        disabled={listPage <= 1}
                        aria-label={t(
                          "admin.roles.mgmt.previousPage",
                          "Previous page"
                        )}
                        onClick={() => setPage(Math.max(1, listPage - 1))}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {listPage} / {listTotalPages}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 rounded-lg px-2"
                        disabled={listPage >= listTotalPages}
                        aria-label={t("admin.roles.mgmt.nextPage", "Next page")}
                        onClick={() =>
                          setPage(Math.min(listTotalPages, listPage + 1))
                        }
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </>
        )}
      </section>

      {/* Invite a colleague. AdminInviteTeamMemberCard already existed and
          already called admin-invite-user, but nothing ever rendered it — the
          only "Invite" control on this page scrolled to the owner-creation
          form. On a staff & tenant directory, Invite should invite staff. */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent
          className="max-w-3xl max-h-[92vh] overflow-y-auto p-0 gap-0"
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">
            {t("admin.roles.invite.title", "Invite team member")}
          </DialogTitle>
          <AdminInviteTeamMemberCard
            isSuperAdmin={isSuperAdmin}
            properties={properties}
            allowOwnerRole={!ownersExcluded}
            onInvited={() => setInviteOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {editDialogRow && (
        <EditUserDialog
          row={editDialogRow}
          properties={properties}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setEditDialogRow(null)}
          onSaved={() => {
            queryClient.invalidateQueries({
              queryKey: ["admin-role-mgmt-users"],
            });
            queryClient.invalidateQueries({
              queryKey: ["admin-settings-role-overview"],
            });
          }}
        />
      )}

      {inviteRow && (
        <OwnerInviteDialog
          row={inviteRow}
          properties={properties}
          onClose={() => setInviteRow(null)}
          onSend={(propertyId, templateIds) =>
            sendOnboardingLink(
              inviteRow.email,
              propertyId,
              inviteRow.name ?? undefined,
              templateIds
            )
          }
        />
      )}

      <Dialog
        open={!!pwTarget}
        onOpenChange={(open) => {
          if (!open) closePwModal();
        }}
      >
        <DialogContent className="rounded-2xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {pwTarget?.mode === "self"
                ? t(
                    "admin.roles.password.changeYourPassword",
                    "Change your password"
                  )
                : pwTarget?.mode === "reset"
                  ? t("admin.owners.actions.resetPassword", "Reset password")
                  : t("admin.owners.actions.setPassword", "Set password")}
            </DialogTitle>
            <DialogDescription>
              {pwTarget?.mode === "self"
                ? t(
                    "admin.roles.password.selfDescription",
                    "Updates the password for your signed-in admin account."
                  )
                : pwTarget?.mode === "reset"
                  ? t(
                      "admin.roles.password.resetDescription",
                      "Resets the password for {email}. No email notification will be sent."
                    ).replace("{email}", pwTarget.email)
                  : pwTarget
                    ? t(
                        "admin.roles.password.setDescription",
                        "Sets a new login password for {email}. They can change it after signing in."
                      ).replace("{email}", pwTarget.email)
                    : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid gap-2">
              <Label htmlFor="admin-pw-new">
                {t("admin.roles.password.newPassword", "New password")}
              </Label>
              <div className="relative">
                <Input
                  id="admin-pw-new"
                  type={showPwNew ? "text" : "password"}
                  autoComplete="new-password"
                  value={pwNew}
                  onChange={(e) => setPwNew(e.target.value)}
                  disabled={pwBusy}
                  className={cn(
                    "rounded-xl pr-10",
                    pwNew.length > 0 &&
                      !pwLenOk &&
                      "border-destructive focus-visible:ring-destructive"
                  )}
                />
                <button
                  type="button"
                  aria-label={
                    showPwNew
                      ? t("admin.roles.password.hidePassword", "Hide password")
                      : t("admin.roles.password.showPassword", "Show password")
                  }
                  onClick={() => setShowPwNew((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {showPwNew ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {pwNew.length > 0 && !pwLenOk ? (
                <p className="text-sm text-destructive">
                  {t(
                    "admin.roles.password.minLengthHint",
                    "Use at least 6 characters."
                  )}
                </p>
              ) : null}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="admin-pw-confirm">
                {t("admin.roles.password.confirmPassword", "Confirm password")}
              </Label>
              <div className="relative">
                <Input
                  id="admin-pw-confirm"
                  type={showPwConfirm ? "text" : "password"}
                  autoComplete="new-password"
                  value={pwConfirm}
                  onChange={(e) => setPwConfirm(e.target.value)}
                  disabled={pwBusy}
                  className={cn(
                    "rounded-xl pr-10",
                    passwordsMismatch &&
                      "border-destructive focus-visible:ring-destructive",
                    passwordsMatch &&
                      "border-emerald-600/50 focus-visible:ring-emerald-600/30"
                  )}
                />
                <button
                  type="button"
                  aria-label={
                    showPwConfirm
                      ? t("admin.roles.password.hidePassword", "Hide password")
                      : t("admin.roles.password.showPassword", "Show password")
                  }
                  onClick={() => setShowPwConfirm((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {showPwConfirm ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {passwordsMismatch ? (
                <p className="text-sm text-destructive">
                  {t(
                    "admin.roles.password.dontMatch",
                    "Passwords don't match."
                  )}
                </p>
              ) : passwordsMatch ? (
                <p className="text-sm text-emerald-700 dark:text-emerald-500 flex items-center gap-1.5">
                  <Check className="h-4 w-4 shrink-0" aria-hidden />
                  {t("admin.roles.password.match", "Passwords match.")}
                </p>
              ) : null}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl"
              onClick={closePwModal}
              disabled={pwBusy}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              type="button"
              className="rounded-xl"
              disabled={!canSavePassword}
              onClick={() => void submitPasswordChange()}
            >
              {pwBusy
                ? t("admin.roles.editUser.saving", "Saving…")
                : t("admin.roles.password.savePassword", "Save password")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!pendingRevoke}
        onOpenChange={(o) => {
          if (!o) setPendingRevoke(null);
        }}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("admin.roles.revoke.title", "Remove elevated access?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "admin.roles.revoke.description",
                "This revokes operational roles for {email}. The account will fall back to a standard tenant account."
              ).replace("{email}", pendingRevoke?.email ?? "")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">
              {t("common.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-xl"
              onClick={() => {
                if (!pendingRevoke || !users) return;
                const row = users.find((u) => u.id === pendingRevoke.userId);
                setPendingRevoke(null);
                if (row) void flushSave(row, true);
              }}
            >
              {t("admin.roles.revoke.confirm", "Confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => {
          if (!o && !deleting) setPendingDelete(null);
        }}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("admin.roles.deleteUser.title", "Delete user permanently?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "admin.roles.deleteUser.descriptionPrefix",
                "This will permanently delete"
              )}{" "}
              <strong>{pendingDelete?.email}</strong>{" "}
              {t(
                "admin.roles.deleteUser.descriptionSuffix",
                "and remove all their roles and data. This action cannot be undone."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" disabled={deleting}>
              {t("common.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-xl bg-destructive hover:bg-destructive/90"
              disabled={deleting}
              onClick={() => void confirmDelete()}
            >
              {deleting
                ? t("admin.roles.deleteUser.deleting", "Deleting…")
                : t(
                    "admin.roles.deleteUser.deletePermanently",
                    "Delete permanently"
                  )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!pendingToggleActive}
        onOpenChange={(o) => {
          if (!o && !togglingActive) setPendingToggleActive(null);
        }}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingToggleActive?.activate
                ? t(
                    "admin.owners.actions.activateConfirmTitle",
                    "Activate account?"
                  )
                : t(
                    "admin.owners.actions.deactivateConfirmTitle",
                    "Deactivate account?"
                  )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingToggleActive?.activate ? (
                <>
                  <strong>{pendingToggleActive.email}</strong>{" "}
                  {t(
                    "admin.roles.toggleActive.willSignInAgain",
                    "will be able to sign in again."
                  )}
                </>
              ) : (
                <>
                  <strong>{pendingToggleActive?.email}</strong>{" "}
                  {t(
                    "admin.roles.toggleActive.willBeSignedOut",
                    "will be immediately signed out and blocked from logging in. Their data is preserved."
                  )}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" disabled={togglingActive}>
              {t("common.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(
                "rounded-xl",
                pendingToggleActive?.activate
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-orange-500 hover:bg-orange-600"
              )}
              disabled={togglingActive}
              onClick={() => void confirmToggleActive()}
            >
              {togglingActive
                ? t("admin.roles.toggleActive.updating", "Updating…")
                : pendingToggleActive?.activate
                  ? t("admin.owners.actions.activate", "Activate")
                  : t("admin.owners.actions.deactivate", "Deactivate")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
